import { buildPocoRankingColumns, getPocoRankingRoundCount } from "../utils/pocoRankingPresentation";
import { useChallengeResultsRefresh } from "../hooks/useChallengeResultsRefresh";
import { selectCategoryPairs, selectCategoryRounds } from "../utils/challengeCategoryResults";
import { canRegisterInAnotherCategory } from "../../events/shared/utils/registrationCategories";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../../../shared/components/AppLayout";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingPage from "../../../shared/components/LoadingPage";
import Spinner from "../../../shared/components/Spinner";
import UserAvatar from "../../../shared/components/UserAvatar";
import EventLocationActions from "../../../shared/components/EventLocationActions";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import ResponsiveRankingTable, {
  ResponsiveRankingColumn,
  ResponsiveRankingRow,
} from "../../../shared/components/ResponsiveRankingTable";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { getSiteLanguage } from "../../../shared/i18n/language";
import { formatEventDateRangeLabel } from "../../../shared/utils/dateTime";
import { buildCompetitionShareUrl } from "../../../shared/lib/shareLinks";
import { organizerIdentityStyle } from "../../../shared/utils/organizerVisualIdentity";
import { useAuth } from "../../auth/shared/hooks/useAuth";
import usePublicOrganizerVisualIdentityTheme from "../../auth/shared/hooks/usePublicOrganizerVisualIdentityTheme";
import { buildLoginHref } from "../../../shared/lib/loginRedirect";
import {
  EventChallengeLink,
  EventRegistration,
  getEventChallengeLink,
  getMyEventRegistration,
} from "../../events/shared/services/eventRegistrationService";
import {
  getChallengeById,
  getChallengeByIdMaybeAuth,
  getPublicTournamentBracket,
  getPublicTournamentEventSchedule,
  getPublicTournamentSchedule,
  type PublicTournamentBracketMatch,
  type PublicTournamentScheduleSlot,
  type TournamentEventSchedule,
} from "../services/challengeAdminService";
import { ChallengeDetail, ChallengeMatch } from "../types/challengeAdmin";
import { normalizePairId } from "../utils/challengePairs";
import {
  getPocoTotalPlayersCapacity,
  resolvePocoPoints,
} from "../utils/pocoSchedule";
import { sortRegistrationCategories } from "../utils/registrationCategoryOrder";
import ChallengeShareModal, {
  ChallengeShareData,
} from "../components/ChallengeShareModal";
import PublicTournamentProgressPanel from "../components/PublicTournamentProgressPanel";
import TournamentEventScheduleView from "../components/TournamentEventScheduleView";
import "./ChallengeDetailPage.css";

type ChallengeDetailPageProps = {
  challengeId: number;
  challengeViewGroup?: "A" | "B";
  origin?: "challenges" | "tournaments";
};

type PublicStatusTone = "pending" | "started" | "finished" | "canceled";
type RegistrationStatusTone = "pending" | "confirmed" | "waitlist";
type DetailTab =
  | "registrations"
  | "matches"
  | "ranking"
  | "brackets"
  | "eventSchedule"
  | "timeline";

type RankingRow = {
  id: string;
  label: string;
  points: number;
  roundPoints: number[];
  isPocoChampion?: boolean;
  isPocoFinalLoser?: boolean;
  gamesWon: number;
  gamesLost: number;
  gamesBalance: number;
  wins: number;
  losses: number;
  matches: number;
};

type MobileRankingSummaryKind = "points" | "wins" | "balance";

type RegistrationRow = {
  key: string;
  position: number;
  name: string;
  avatars: Array<{
    avatarUrl: string | null;
    initials: string;
    name: string;
    publicPlayerId?: number | null;
    sex: "male" | "female" | "other" | "";
  }>;
  participantCount: number;
  statusLabel: string;
  statusTone: RegistrationStatusTone;
  paymentUrl: string | null;
  registrationCategoryKey: string | null;
  registrationCategoryName: string | null;
};

type PublicRegistration = NonNullable<ChallengeDetail["registrations"]>[number];

type ChallengeAvatarItem = {
  avatarUrl: string;
  name: string;
  sex: "male" | "female" | "other" | "";
  publicPlayerId?: number | null;
  profileHref?: string;
};

type MyGameItem = {
  id: string;
  categoryName: string;
  pairLabel: string;
  opponentLabel: string;
  phaseLabel: string;
  dateLabel: string;
  timeLabel: string;
  courtLabel: string;
  statusLabel: string;
  statusKey: "waiting" | "scheduled" | "in-progress" | "finished" | "wo";
  timestamp: number;
  isFinished: boolean;
  hasSchedule: boolean;
};

type CategorySelectOption = {
  key: string;
  label: string;
};

type CategorySelectDropdownProps = {
  ariaLabel: string;
  onChange: (value: string) => void;
  options: CategorySelectOption[];
  value: string;
};

function CategorySelectDropdown({
  ariaLabel,
  onChange,
  options,
  value,
}: CategorySelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const selectedOption =
    options.find((option) => option.key === value) || options[0] || null;

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        wrapperRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  return (
    <span
      className={`challenge-public-category-select${open ? " is-open" : ""}`}
      ref={wrapperRef}
    >
      <button
        type="button"
        className="challenge-public-category-select-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <span className="challenge-public-category-select-value">
          {selectedOption?.label || ""}
        </span>
        <span
          className="challenge-public-category-select-chevron material-symbols-outlined"
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>
      {open && (
        <span
          className="challenge-public-category-select-menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const active = option.key === value;
            return (
              <button
                key={option.key || "all"}
                type="button"
                className={`challenge-public-category-select-option${
                  active ? " is-active" : ""
                }`}
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}

const challengeDetailTexts = siteTexts.challenges.publicDetail;
const LOCALE_BY_LANGUAGE = { pt: "pt-BR", en: "en-US", es: "es-ES" } as const;

function addHoursToIsoString(value: string, hoursToAdd: number) {
  const parsed = new Date(String(value || ""));
  if (Number.isNaN(parsed.getTime())) return String(value || "");
  parsed.setHours(parsed.getHours() + hoursToAdd);
  return parsed.toISOString();
}

function normalizeCpfDigits(value?: string | null) {
  return String(value || "").replace(/\D/g, "");
}

function publicPlayerHref(playerId?: number | null) {
  const safeId = Number(playerId || 0);
  return Number.isFinite(safeId) && safeId > 0
    ? `#/jogadores/${safeId}`
    : undefined;
}

function myGameStatusMeta(status?: string | null): {
  label: string;
  key: MyGameItem["statusKey"];
  finished: boolean;
} {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") {
    return {
      label: challengeDetailTexts.myGames.finished,
      key: "finished",
      finished: true,
    };
  }
  if (normalized === "in_progress" || normalized === "started") {
    return {
      label: challengeDetailTexts.myGames.inProgress,
      key: "in-progress",
      finished: false,
    };
  }
  if (normalized === "wo" || normalized === "walkover") {
    return { label: "WO", key: "wo", finished: true };
  }
  if (normalized === "scheduled") {
    return {
      label: challengeDetailTexts.myGames.waiting,
      key: "scheduled",
      finished: false,
    };
  }
  return {
    label: challengeDetailTexts.myGames.waiting,
    key: "waiting",
    finished: false,
  };
}

function myGameRoundLabel(
  roundCode?: string | null,
  roundNumber?: number | null,
) {
  const normalized = String(roundCode || "")
    .trim()
    .toLowerCase();
  const texts = challengeDetailTexts.myGames;
  if (normalized === "round_of_32") return texts.roundOf32;
  if (normalized === "round_of_16") return texts.roundOf16;
  if (normalized === "round_of_8" || normalized === "quarterfinal")
    return texts.quarterfinal;
  if (normalized === "round_of_4" || normalized === "semifinal")
    return texts.semifinal;
  if (normalized === "round_of_2" || normalized === "final") return texts.final;
  return Number(roundNumber || 0) > 0
    ? `${texts.roundPrefix} ${roundNumber}`
    : texts.gameFallback;
}

function myGameCourtLabel(value?: string | null) {
  const court = String(value || "").trim();
  return court && court !== "A definir"
    ? court
    : challengeDetailTexts.myGames.courtFallback;
}

function myGameDateParts(value?: string | null) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return {
      dateLabel: challengeDetailTexts.myGames.dateFallback,
      timeLabel: challengeDetailTexts.myGames.timeFallback,
      timestamp: Number.MAX_SAFE_INTEGER,
      hasSchedule: false,
    };
  }
  return {
    dateLabel: new Intl.DateTimeFormat(
      LOCALE_BY_LANGUAGE[getSiteLanguage()],
    ).format(parsed),
    timeLabel: new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[getSiteLanguage()], {
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsed),
    timestamp: parsed.getTime(),
    hasSchedule: true,
  };
}

function buildInternalEventIdByTournament(tournamentId: number) {
  if (!Number.isFinite(tournamentId) || tournamentId <= 0) return "";
  return `ev_t_${Math.floor(tournamentId)}`;
}

function categoryLabel(code?: string | null) {
  if (!code) return challengeDetailTexts.labels.toBeDefined;
  if (code === "7a") return siteTexts.common.category.c7a;
  if (code === "6a") return siteTexts.common.category.c6a;
  if (code === "5a") return siteTexts.common.category.c5a;
  if (code === "4a") return siteTexts.common.category.c4a;
  if (code === "3a") return siteTexts.common.category.c3a;
  if (code === "2a") return siteTexts.common.category.c2a;
  if (code === "open") return siteTexts.common.category.open;
  if (code === "free") return siteTexts.common.category.free;
  return String(code || "").toUpperCase();
}

function formatCurrency(cents: number | null | undefined) {
  if (!Number.isFinite(Number(cents)))
    return challengeDetailTexts.labels.toBeDefined;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(cents) / 100);
}

function challengeFormatLabel(type?: string | null) {
  if (type === "super8") return challengeDetailTexts.format.super8;
  if (type === "poco") return challengeDetailTexts.format.poco;
  if (type === "tournament") return challengeDetailTexts.format.tournament;
  return challengeDetailTexts.format.challenge;
}

function isFixedPairMode(value?: string | null) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return (
    normalized === "fixed_pair" ||
    normalized === "dupla_fixa" ||
    normalized === "fixedpair" ||
    normalized === "duplafixa"
  );
}

function initialsFromName(value?: string | null) {
  const label = String(value || "").trim();
  if (!label)
    return challengeDetailTexts.labels.playerFallback.slice(0, 2).toUpperCase();
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() || "")
    .join("");
  return (
    initials ||
    challengeDetailTexts.labels.playerFallback.slice(0, 2).toUpperCase()
  );
}

function normalizeAvatarSex(
  value?: string | null,
): "male" | "female" | "other" | "" {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (normalized === "male" || normalized === "masculino") return "male";
  if (normalized === "female" || normalized === "feminino") return "female";
  if (normalized === "other" || normalized === "outro") return "other";
  return "";
}

function splitPairLabel(value?: string | null) {
  return String(value || "")
    .split(" / ")
    .map((item) => item.trim())
    .filter(Boolean);
}

function ChallengeMatchParticipant({
  avatars,
  label,
  side,
}: {
  avatars: ChallengeAvatarItem[];
  label: string;
  side: "first" | "second";
}) {
  if (avatars.length === 0) {
    return <span className="challenge-public-match-pair">{label}</span>;
  }

  const labelParts = splitPairLabel(label);
  return (
    <span
      className={`challenge-public-match-participant challenge-public-match-participant--${side}${
        avatars.length > 1 ? " is-pair" : ""
      }`}
    >
      {avatars.map((avatar, index) => (
        <span
          key={`${side}-${avatar.name}-${index}`}
          className="challenge-public-match-member"
        >
          <UserAvatar
            className="challenge-public-match-avatar"
            name={avatar.name}
            sex={avatar.sex}
            avatarUrl={avatar.avatarUrl}
            size={42}
          />
          <span>{labelParts[index] || avatar.name}</span>
        </span>
      ))}
    </span>
  );
}

function fixedPairDisplayCount(value: number) {
  return Math.max(0, Math.floor(Math.max(0, Number(value) || 0) / 2));
}

function registrationUnitCount(value: number, fixedPair: boolean) {
  return fixedPair
    ? fixedPairDisplayCount(value)
    : Math.max(0, Math.floor(Number(value) || 0));
}

function isIndividualMode(value?: string | null) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "individual";
}

function inferFixedPairFromChallenge(challenge?: ChallengeDetail | null) {
  if (!challenge) return false;
  const pairs = challenge.pairs || [];
  if (pairs.length === 0) return false;

  const cpfCounts = new Map<string, number>();
  pairs.forEach((pair) => {
    [pair.cpf1, pair.cpf2].forEach((cpfRaw) => {
      const cpf = String(cpfRaw || "").trim();
      if (!cpf) return;
      cpfCounts.set(cpf, (cpfCounts.get(cpf) || 0) + 1);
    });
  });

  if (cpfCounts.size === 0) return false;
  const everyCpfAppearsOnce = Array.from(cpfCounts.values()).every(
    (count) => count === 1,
  );
  if (!everyCpfAppearsOnce) return false;

  const participantCpfs = new Set(
    (challenge.participantCpfs || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean),
  );
  if (participantCpfs.size === 0) return false;
  if (participantCpfs.size !== cpfCounts.size) return false;
  return Array.from(cpfCounts.keys()).every((cpf) => participantCpfs.has(cpf));
}

export function minimumParticipantsByType(item: ChallengeDetail) {
  if (item.challengeType === "tournament") {
    const slotLimit = Number(item.slotLimit || 0);
    if (Number.isFinite(slotLimit) && slotLimit > 0) {
      return Math.floor(slotLimit) * 2;
    }
    return 0;
  }
  if (item.challengeType === "super8") return 8;
  if (item.challengeType === "poco") {
    return getPocoTotalPlayersCapacity({
      courtsCount: item.courtsCount,
      playersCount: item.playersCount,
      registrationCategories: item.registrationCategories,
    });
  }
  return 12;
}

function resolvePublicStatusMeta(statusRaw: string) {
  const status = String(statusRaw || "")
    .trim()
    .toLowerCase();
  if (status === "pending") {
    return {
      tone: "pending" as PublicStatusTone,
      label: challengeDetailTexts.statuses.pending,
    };
  }
  if (status === "started") {
    return {
      tone: "started" as PublicStatusTone,
      label: challengeDetailTexts.statuses.started,
    };
  }
  if (status === "finished") {
    return {
      tone: "finished" as PublicStatusTone,
      label: challengeDetailTexts.statuses.finished,
    };
  }
  if (status === "canceled" || status === "cancelado") {
    return {
      tone: "canceled" as PublicStatusTone,
      label: challengeDetailTexts.statuses.canceled,
    };
  }
  return {
    tone: "pending" as PublicStatusTone,
    label: challengeDetailTexts.statuses.undefined,
  };
}

function parseScore(score: number | "" | undefined) {
  const value = Number(score);
  return Number.isFinite(value) ? value : null;
}

function resolvePocoWinnerSide(match: ChallengeMatch) {
  const score1 = parseScore(match?.score1);
  const score2 = parseScore(match?.score2);
  if (score1 === null || score2 === null || score1 === score2) return null;
  return score1 > score2 ? "dupla1" : "dupla2";
}

function isPendingPocoMatch(match: ChallengeMatch) {
  const dupla1Id = String(match?.dupla1Id || "").trim();
  const dupla2Id = String(match?.dupla2Id || "").trim();
  if (!dupla1Id || !dupla2Id) return true;
  const pendingLabel = String(
    siteTexts.challengeEngine.labels.removedPair || "",
  )
    .trim()
    .toLowerCase();
  const label1 = String((match as any)?.dupla1Label || "")
    .trim()
    .toLowerCase();
  const label2 = String((match as any)?.dupla2Label || "")
    .trim()
    .toLowerCase();
  const isPendingLabel = (value: string) =>
    value === pendingLabel || value === "dupla removida";
  return isPendingLabel(label1) && isPendingLabel(label2);
}

function isPendingPairLabel(value: unknown) {
  const pendingLabel = String(
    siteTexts.challengeEngine.labels.removedPair || "",
  )
    .trim()
    .toLowerCase();
  const label = String(value || "")
    .trim()
    .toLowerCase();
  return !label || label === pendingLabel || label === "dupla removida";
}

function calculateRanking(
  pairs: ChallengeDetail["pairs"],
  matches: Array<ChallengeMatch & { dupla1Label: string; dupla2Label: string }>,
) {
  const headToHead = (pairA: string, pairB: string) => {
    const match = matches.find((item) => {
      const dupla1Id = normalizePairId(item.dupla1Id);
      const dupla2Id = normalizePairId(item.dupla2Id);
      return (
        (dupla1Id === pairA && dupla2Id === pairB) ||
        (dupla1Id === pairB && dupla2Id === pairA)
      );
    });
    if (!match) return 0;

    const score1 = parseScore(match.score1);
    const score2 = parseScore(match.score2);
    if (score1 === null || score2 === null) return 0;

    const pairAScore =
      normalizePairId(match.dupla1Id) === pairA ? score1 : score2;
    const pairBScore =
      normalizePairId(match.dupla1Id) === pairA ? score2 : score1;
    if (pairAScore === pairBScore) return 0;
    return pairAScore > pairBScore ? 1 : -1;
  };

  return pairs
    .map<RankingRow>((pair) => {
      const pairId = normalizePairId(pair.id);
      let gamesWon = 0;
      let gamesLost = 0;
      let wins = 0;
      let losses = 0;

      matches.forEach((match) => {
        const score1 = parseScore(match.score1);
        const score2 = parseScore(match.score2);
        if (score1 === null || score2 === null) return;

        if (normalizePairId(match.dupla1Id) === pairId) {
          gamesWon += score1;
          gamesLost += score2;
          if (score1 > score2) wins += 1;
          if (score1 < score2) losses += 1;
        }
        if (normalizePairId(match.dupla2Id) === pairId) {
          gamesWon += score2;
          gamesLost += score1;
          if (score2 > score1) wins += 1;
          if (score2 < score1) losses += 1;
        }
      });

      return {
        id: pairId || pair.id,
        label: pair.label,
        points: 0,
        roundPoints: [],
        gamesWon,
        gamesLost,
        gamesBalance: gamesWon - gamesLost,
        wins,
        losses,
        matches: wins + losses,
      };
    })
    .sort((a, b) => {
      if (b.gamesBalance !== a.gamesBalance)
        return b.gamesBalance - a.gamesBalance;
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
      if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
      const h2h = headToHead(a.id, b.id);
      if (h2h !== 0) return -h2h;
      const labelDiff = a.label.localeCompare(b.label, "pt-BR");
      if (labelDiff !== 0) return labelDiff;
      return String(a.id || "").localeCompare(String(b.id || ""), "pt-BR");
    });
}

function calculatePocoRanking(
  pairs: ChallengeDetail["pairs"],
  rounds: ChallengeDetail["roundsDetailed"],
) {
  const totalRounds = getPocoRankingRoundCount(rounds);
  const finalRoundIndex = totalRounds > 0 ? totalRounds - 1 : -1;
  const lastScoringRoundIndex =
    finalRoundIndex > 1 ? finalRoundIndex - 1 : totalRounds - 1;
  let finalWinnerId = "";
  let finalLoserId = "";
  const finalMatch =
    finalRoundIndex >= 0 && Array.isArray(rounds?.[finalRoundIndex])
      ? rounds[finalRoundIndex][0]
      : null;
  if (finalMatch) {
    const score1 = parseScore(finalMatch.score1);
    const score2 = parseScore(finalMatch.score2);
    const dupla1Id = normalizePairId(finalMatch.dupla1Id);
    const dupla2Id = normalizePairId(finalMatch.dupla2Id);
    if (
      score1 !== null &&
      score2 !== null &&
      score1 !== score2 &&
      dupla1Id &&
      dupla2Id
    ) {
      finalWinnerId = score1 > score2 ? dupla1Id : dupla2Id;
      finalLoserId = score1 > score2 ? dupla2Id : dupla1Id;
    }
  }

  const rowsById = new Map<string, RankingRow>();
  (pairs || []).forEach((pair) => {
    const pairId = normalizePairId(pair.id);
    if (!pairId) return;
    rowsById.set(pairId, {
      id: pairId || pair.id,
      label: pair.label,
      points: 0,
      roundPoints: [],
      gamesWon: 0,
      gamesLost: 0,
      gamesBalance: 0,
      wins: 0,
      losses: 0,
      matches: 0,
    });
  });

  (rounds || []).forEach((round, roundIndex) => {
    (round || []).forEach((match, courtIndex) => {
      const score1 = parseScore(match.score1);
      const score2 = parseScore(match.score2);
      if (score1 === null || score2 === null || score1 === score2) return;

      const row1 = rowsById.get(normalizePairId(match.dupla1Id));
      const row2 = rowsById.get(normalizePairId(match.dupla2Id));
      if (!row1 || !row2) return;

      row1.gamesWon += score1;
      row1.gamesLost += score2;
      row2.gamesWon += score2;
      row2.gamesLost += score1;
      row1.matches += 1;
      row2.matches += 1;

      const pair1Won = score1 > score2;
      if (pair1Won) {
        row1.wins += 1;
        row2.losses += 1;
      } else {
        row2.wins += 1;
        row1.losses += 1;
      }

      if (roundIndex > 0 && roundIndex < finalRoundIndex) {
        const pair1RoundPoints = resolvePocoPoints(
          courtIndex,
          pair1Won,
          round.length,
        );
        const pair2RoundPoints = resolvePocoPoints(
          courtIndex,
          !pair1Won,
          round.length,
        );
        row1.points += pair1RoundPoints;
        row2.points += pair2RoundPoints;
        row1.roundPoints[roundIndex] =
          Number(row1.roundPoints[roundIndex] || 0) + pair1RoundPoints;
        row2.roundPoints[roundIndex] =
          Number(row2.roundPoints[roundIndex] || 0) + pair2RoundPoints;
      }
    });
  });

  const compareByPoints = (a: RankingRow, b: RankingRow) => {
    if (b.points !== a.points) return b.points - a.points;
    const maxRoundIndex = Math.max(
      Math.min(a.roundPoints.length - 1, lastScoringRoundIndex),
      Math.min(b.roundPoints.length - 1, lastScoringRoundIndex),
    );
    for (let roundIndex = maxRoundIndex; roundIndex >= 1; roundIndex -= 1) {
      const diff =
        Number(b.roundPoints[roundIndex] || 0) -
        Number(a.roundPoints[roundIndex] || 0);
      if (diff !== 0) return diff;
    }
    return String(a.label || "").localeCompare(String(b.label || ""));
  };

  const baseRanking = Array.from(rowsById.values())
    .map((row) => ({
      ...row,
      isPocoChampion: finalWinnerId ? row.id === finalWinnerId : false,
      isPocoFinalLoser: finalLoserId ? row.id === finalLoserId : false,
      gamesBalance: row.gamesWon - row.gamesLost,
    }))
    .sort(compareByPoints);

  if (!finalWinnerId || !finalLoserId) {
    return baseRanking;
  }

  return [...baseRanking].sort((a, b) => {
    const priorityA =
      a.id === finalWinnerId ? 0 : a.id === finalLoserId ? 1 : 2;
    const priorityB =
      b.id === finalWinnerId ? 0 : b.id === finalLoserId ? 1 : 2;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return compareByPoints(a, b);
  });
}

function calculateSuper8Ranking(
  participants: ChallengeDetail["participants"],
  pairs: ChallengeDetail["pairs"],
  matches: Array<ChallengeMatch & { dupla1Label: string; dupla2Label: string }>,
) {
  const nameByCpf = (participants || []).reduce<Record<string, string>>(
    (acc, item) => {
      const cpf = String(item?.cpf || "").trim();
      if (!cpf) return acc;
      acc[cpf] =
        String(item?.name || "").trim() ||
        challengeDetailTexts.labels.playerFallback;
      return acc;
    },
    {},
  );

  const pairById = (pairs || []).reduce<
    Record<string, ChallengeDetail["pairs"][number]>
  >((acc, pair) => {
    const pairId = normalizePairId(pair.id);
    if (pairId) acc[pairId] = pair;
    return acc;
  }, {});

  const rows = new Map<string, RankingRow>();
  const directWins = new Map<string, number>();
  const buildDirectKey = (winnerCpf: string, loserCpf: string) =>
    `${String(winnerCpf || "").trim()}::${String(loserCpf || "").trim()}`;
  const ensureRow = (cpf: string) => {
    const safeCpf = String(cpf || "").trim();
    if (!safeCpf) return null;
    const existing = rows.get(safeCpf);
    if (existing) return existing;
    const created: RankingRow = {
      id: safeCpf,
      label: nameByCpf[safeCpf] || challengeDetailTexts.labels.playerFallback,
      points: 0,
      roundPoints: [],
      gamesWon: 0,
      gamesLost: 0,
      gamesBalance: 0,
      wins: 0,
      losses: 0,
      matches: 0,
    };
    rows.set(safeCpf, created);
    return created;
  };

  (participants || []).forEach((item) => {
    const cpf = String(item?.cpf || "").trim();
    if (!cpf) return;
    ensureRow(cpf);
  });

  matches.forEach((match) => {
    const score1 = parseScore(match.score1);
    const score2 = parseScore(match.score2);
    if (score1 === null || score2 === null) return;
    if (score1 === score2) return;

    const pair1 = pairById[normalizePairId(match.dupla1Id)];
    const pair2 = pairById[normalizePairId(match.dupla2Id)];
    if (!pair1 || !pair2) return;

    const pair1Players = [pair1.cpf1, pair1.cpf2]
      .map((cpf) => String(cpf || "").trim())
      .filter(Boolean);
    const pair2Players = [pair2.cpf1, pair2.cpf2]
      .map((cpf) => String(cpf || "").trim())
      .filter(Boolean);

    pair1Players.forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.matches += 1;
      row.gamesWon += score1;
      row.gamesLost += score2;
      if (score1 > score2) row.wins += 1;
      else row.losses += 1;
    });

    pair2Players.forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.matches += 1;
      row.gamesWon += score2;
      row.gamesLost += score1;
      if (score2 > score1) row.wins += 1;
      else row.losses += 1;
    });

    if (score1 > score2) {
      pair1Players.forEach((winnerCpf) => {
        pair2Players.forEach((loserCpf) => {
          const key = buildDirectKey(winnerCpf, loserCpf);
          directWins.set(key, Number(directWins.get(key) || 0) + 1);
        });
      });
    } else {
      pair2Players.forEach((winnerCpf) => {
        pair1Players.forEach((loserCpf) => {
          const key = buildDirectKey(winnerCpf, loserCpf);
          directWins.set(key, Number(directWins.get(key) || 0) + 1);
        });
      });
    }
  });

  const list = Array.from(rows.values()).map((row) => ({
    ...row,
    gamesBalance: row.gamesWon - row.gamesLost,
  }));

  return list.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.gamesBalance !== a.gamesBalance)
      return b.gamesBalance - a.gamesBalance;
    if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
    if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
    const aDirectWins = Number(directWins.get(buildDirectKey(a.id, b.id)) || 0);
    const bDirectWins = Number(directWins.get(buildDirectKey(b.id, a.id)) || 0);
    if (aDirectWins !== bDirectWins) {
      return aDirectWins > bDirectWins ? -1 : 1;
    }
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

function renderChallengeRankingMobileValue(
  row: RankingRow,
  kind: MobileRankingSummaryKind,
) {
  const value =
    kind === "points"
      ? row.points
      : kind === "balance"
        ? row.gamesBalance
        : row.wins;
  const valueLabel =
    kind === "balance" && value > 0 ? `+${value}` : String(value);
  const unit =
    kind === "points"
      ? "pts"
      : kind === "balance"
        ? challengeDetailTexts.ranking.columns.balanceShort
        : challengeDetailTexts.ranking.columns.winsShort;
  return (
    <span className="responsive-ranking-table-points-label">
      <span className="responsive-ranking-table-points-value">{valueLabel}</span>
      <span className="responsive-ranking-table-points-unit">{unit}</span>
    </span>
  );
}

function resolveSuper8RoundGroup(
  roundIndex: number,
  totalRounds: number,
  isSuper8Challenge: boolean,
  participantsCount: number,
  status: string,
): "A" | "B" | null {
  if (!isSuper8Challenge) return null;
  if (status === "pending") return null;
  if (
    Number.isFinite(participantsCount) &&
    participantsCount > 0 &&
    participantsCount <= 8
  ) {
    return null;
  }
  const isCombined = totalRounds === 14;
  if (!isCombined) return null;
  return roundIndex < 7 ? "A" : "B";
}

function resolveChallengeCategory(item: ChallengeDetail) {
  const gender = String(item.categoryGender || "")
    .trim()
    .toLowerCase();
  if (gender === "mixed" || gender === "open") {
    const genderLabel =
      gender === "open"
        ? siteTexts.common.challengeGender.open
        : siteTexts.common.challengeGender.mixed;
    return `${genderLabel} · ${siteTexts.common.challengeGender.maleShort} ${categoryLabel(item.categoryMale)} / ${siteTexts.common.challengeGender.femaleShort} ${categoryLabel(item.categoryFemale)}`;
  }
  if (gender === "male") {
    return `${siteTexts.common.challengeGender.male} · ${categoryLabel(item.category)}`;
  }
  if (gender === "female") {
    return `${siteTexts.common.challengeGender.female} · ${categoryLabel(item.category)}`;
  }
  return categoryLabel(item.category);
}

function resolveRegistrationStatusLabel(input: {
  registrationStatus?: string | null;
  paymentStatus?: string | null;
  paymentRequired?: boolean | null;
}) {
  const registrationStatus = String(input.registrationStatus || "")
    .trim()
    .toLowerCase();
  const paymentStatus = String(input.paymentStatus || "")
    .trim()
    .toLowerCase();
  const paymentRequired = Boolean(input.paymentRequired);
  if (registrationStatus === "waitlist") {
    return challengeDetailTexts.registration.statuses.waitlist;
  }
  if (registrationStatus === "canceled" || paymentStatus === "canceled") {
    return challengeDetailTexts.registration.statuses.canceled;
  }
  if (paymentRequired && paymentStatus !== "confirmed") {
    return challengeDetailTexts.registration.statuses.pendingPayment;
  }
  if (registrationStatus === "confirmed") {
    return challengeDetailTexts.registration.statuses.confirmed;
  }
  return challengeDetailTexts.registration.statuses.pending;
}

function resolveRegistrationStatusTone(input: {
  registrationStatus?: string | null;
  paymentStatus?: string | null;
  paymentRequired?: boolean | null;
}): RegistrationStatusTone {
  const registrationStatus = String(input.registrationStatus || "")
    .trim()
    .toLowerCase();
  const paymentStatus = String(input.paymentStatus || "")
    .trim()
    .toLowerCase();
  const paymentRequired = Boolean(input.paymentRequired);
  if (registrationStatus === "waitlist") return "waitlist";
  if (paymentRequired && paymentStatus !== "confirmed") return "pending";
  if (registrationStatus === "confirmed") {
    return "confirmed";
  }
  return "pending";
}

function registrationParticipantCount(registration: {
  registrationType?: string | null;
  player2Name?: string | null;
  player2Cpf?: string | null;
}) {
  const type = String(registration.registrationType || "")
    .trim()
    .toLowerCase();
  if (
    type === "fixed_pair" ||
    type === "pair" ||
    type === "dupla_fixa" ||
    String(registration.player2Name || "").trim() ||
    String(registration.player2Cpf || "").trim()
  ) {
    return 2;
  }
  return 1;
}

function isActivePublicRegistration(registration: {
  registrationStatus?: string | null;
  paymentStatus?: string | null;
}) {
  const registrationStatus = String(registration.registrationStatus || "")
    .trim()
    .toLowerCase();
  const paymentStatus = String(registration.paymentStatus || "")
    .trim()
    .toLowerCase();
  return (
    registrationStatus !== "canceled" &&
    paymentStatus !== "canceled" &&
    paymentStatus !== "refunded"
  );
}

function isPublicRegistrationPaymentConfirmed(registration: PublicRegistration) {
  return (
    String(registration.paymentStatus || "").trim().toLowerCase() === "confirmed"
  );
}

function publicRegistrationOrderingDate(registration: PublicRegistration) {
  if (isPublicRegistrationPaymentConfirmed(registration)) {
    return (
      registration.paymentConfirmedAt ||
      registration.updatedAt ||
      registration.createdAt ||
      null
    );
  }
  return registration.updatedAt || registration.createdAt || null;
}

function publicRegistrationSortTime(value?: string | null) {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime())
    ? parsed.getTime()
    : Number.POSITIVE_INFINITY;
}

export function comparePublicRegistrationsByPaymentOrder(
  left: PublicRegistration,
  right: PublicRegistration,
) {
  const confirmedLeft = isPublicRegistrationPaymentConfirmed(left) ? 0 : 1;
  const confirmedRight = isPublicRegistrationPaymentConfirmed(right) ? 0 : 1;
  if (confirmedLeft !== confirmedRight) {
    return confirmedLeft - confirmedRight;
  }

  const leftTime = publicRegistrationSortTime(
    publicRegistrationOrderingDate(left),
  );
  const rightTime = publicRegistrationSortTime(
    publicRegistrationOrderingDate(right),
  );
  if (leftTime !== rightTime) return leftTime - rightTime;

  return Number(left.id || 0) - Number(right.id || 0);
}

export default function ChallengeDetailPage({
  challengeId,
  challengeViewGroup,
  origin = "challenges",
}: ChallengeDetailPageProps) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [challenge, setChallenge] = useState<ChallengeDetail | null>(null);
  const [challengeLink, setChallengeLink] = useState<EventChallengeLink | null>(
    null,
  );
  const [myRegistration, setMyRegistration] =
    useState<EventRegistration | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("registrations");
  const [selectedTournamentCategoryKey, setSelectedTournamentCategoryKey] =
    useState("");
  const [isMobileInfoOpen, setIsMobileInfoOpen] = useState(false);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const tabContentRef = useRef<HTMLDivElement | null>(null);
  const [myGamesOpen, setMyGamesOpen] = useState(false);
  const [myTournamentGames, setMyTournamentGames] = useState<MyGameItem[]>([]);
  const [myTournamentGamesLoading, setMyTournamentGamesLoading] =
    useState(false);
  const [myTournamentGamesError, setMyTournamentGamesError] = useState("");
  const [tournamentEventSchedule, setTournamentEventSchedule] =
    useState<TournamentEventSchedule | null>(null);
  const [
    publishedTournamentBracketMatchesCount,
    setPublishedTournamentBracketMatchesCount,
  ] = useState(0);
  const listBasePath = origin === "tournaments" ? "#/torneios" : "#/desafios";
  const refreshingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsAboutExpanded(false);
  }, [challengeId]);

  const fetchChallenge = useCallback(
    async ({ showLoading }: { showLoading: boolean }) => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      if (showLoading && mountedRef.current) setLoading(true);
      try {
        if (mountedRef.current) setLoadError("");
        let data: ChallengeDetail;
        try {
          data = await getChallengeByIdMaybeAuth(challengeId, token);
        } catch (_publicErr) {
          if (token) {
            try {
              data = await getChallengeById(token, challengeId);
            } catch (_adminErr) {
              data = await getChallengeByIdMaybeAuth(challengeId);
            }
          } else {
            throw _publicErr;
          }
        }
        if (!mountedRef.current) return;
        setChallenge(data);
      } catch (_err) {
        if (!mountedRef.current) return;
        setLoadError(
          _err instanceof Error
            ? _err.message
            : challengeDetailTexts.states.loadErrorFallback,
        );
        if (showLoading) setChallenge(null);
      } finally {
        if (mountedRef.current && showLoading) setLoading(false);
        refreshingRef.current = false;
      }
    },
    [challengeId, token],
  );

  useEffect(() => {
    void fetchChallenge({ showLoading: true });
  }, [fetchChallenge]);

  const refreshResults = useCallback(() => fetchChallenge({ showLoading: false }), [fetchChallenge]);
  useChallengeResultsRefresh(challenge?.challengeType === 'poco' && challenge?.status === 'started', refreshResults);

  const displayedChallenge = useMemo(() => {
    if (!challenge) return null;
    const totalRoundsDetailed = Array.isArray(challenge.roundsDetailed)
      ? challenge.roundsDetailed.length
      : 0;
    const totalRoundsRaw = Array.isArray(challenge.rounds)
      ? challenge.rounds.length
      : 0;
    const isCombinedSuper8 =
      challenge.challengeType === "super8" &&
      (challenge.status === "pending"
        ? Number(challenge.participantsCount || 0) > 8 &&
          totalRoundsDetailed === 0 &&
          totalRoundsRaw === 0
        : totalRoundsDetailed === 14 || totalRoundsRaw === 14);
    const targetGroup =
      isCombinedSuper8 &&
      (challengeViewGroup === "A" || challengeViewGroup === "B")
        ? challengeViewGroup
        : null;
    if (!targetGroup) {
      if (challenge.challengeType !== "super8" || isCombinedSuper8)
        return challenge;
      return { ...challenge, super8Group: null };
    }

    const roundsDetailedSplit = Math.ceil(totalRoundsDetailed / 2);
    const roundsRawSplit = Math.ceil(totalRoundsRaw / 2);
    const roundsDetailedSlice =
      targetGroup === "A"
        ? (challenge.roundsDetailed || []).slice(0, roundsDetailedSplit)
        : (challenge.roundsDetailed || []).slice(roundsDetailedSplit);
    const roundsSlice =
      targetGroup === "A"
        ? (challenge.rounds || []).slice(0, roundsRawSplit)
        : (challenge.rounds || []).slice(roundsRawSplit);

    const pairIdsInGroup = new Set<string>();
    roundsDetailedSlice.forEach((round) => {
      round.forEach((match) => {
        const dupla1Id = normalizePairId(match.dupla1Id);
        const dupla2Id = normalizePairId(match.dupla2Id);
        if (dupla1Id) pairIdsInGroup.add(dupla1Id);
        if (dupla2Id) pairIdsInGroup.add(dupla2Id);
      });
    });

    const orderedParticipantCpfs = (challenge.participantCpfs || [])
      .map((cpf) => String(cpf || "").trim())
      .filter(Boolean);
    const super8GroupSplitIndex = Math.min(8, orderedParticipantCpfs.length);
    const participantCpfsGroup =
      targetGroup === "A"
        ? orderedParticipantCpfs.slice(0, super8GroupSplitIndex)
        : orderedParticipantCpfs.slice(
            super8GroupSplitIndex,
            super8GroupSplitIndex + 8,
          );

    const pairsGroup =
      pairIdsInGroup.size > 0
        ? (challenge.pairs || []).filter((pair) =>
            pairIdsInGroup.has(normalizePairId(pair.id)),
          )
        : (challenge.pairs || []).filter(
            (pair) =>
              participantCpfsGroup.includes(pair.cpf1) &&
              participantCpfsGroup.includes(pair.cpf2),
          );
    const participantCpfsFromPairs = new Set<string>();
    pairsGroup.forEach((pair) => {
      if (pair.cpf1) participantCpfsFromPairs.add(pair.cpf1);
      if (pair.cpf2) participantCpfsFromPairs.add(pair.cpf2);
    });

    const participantCpfsFinal =
      participantCpfsFromPairs.size > 0
        ? Array.from(participantCpfsFromPairs)
        : participantCpfsGroup;
    const participantsGroup = (challenge.participants || []).filter((item) =>
      participantCpfsFinal.includes(item.cpf),
    );

    return {
      ...challenge,
      super8Group: targetGroup,
      participantCpfs: participantCpfsFinal,
      participantsCount: participantCpfsFinal.length,
      rounds: roundsSlice,
      roundsDetailed: roundsDetailedSlice,
      pairs: pairsGroup,
      participants: participantsGroup,
    };
  }, [challenge, challengeViewGroup]);

  const organizerVisualIdentity = usePublicOrganizerVisualIdentityTheme(
    displayedChallenge?.organizerUserId,
    displayedChallenge?.isOfficial,
  );

  const challengeStatusMeta = useMemo(
    () =>
      resolvePublicStatusMeta(String(displayedChallenge?.status || "pending")),
    [displayedChallenge?.status],
  );

  const subtitleDate = useMemo(() => {
    if (!displayedChallenge) return "";
    const baseDate =
      displayedChallenge.scheduledAt || displayedChallenge.createdAt;
    if (
      displayedChallenge.challengeType === "super8" &&
      displayedChallenge.super8Group === "B"
    ) {
      return formatEventDateRangeLabel(
        addHoursToIsoString(baseDate, 2),
        displayedChallenge.scheduledEndAt
          ? addHoursToIsoString(displayedChallenge.scheduledEndAt, 2)
          : null,
      );
    }
    return formatEventDateRangeLabel(
      baseDate,
      displayedChallenge.scheduledEndAt,
    );
  }, [displayedChallenge]);

  const linkedEventId = useMemo(
    () => buildInternalEventIdByTournament(challengeId),
    [challengeId],
  );

  const eventDetailsHref = useMemo(() => {
    if (!linkedEventId) return listBasePath;
    const target = `#/eventos/id/${encodeURIComponent(linkedEventId)}/inscricao`;
    return token ? target : buildLoginHref(target);
  }, [linkedEventId, listBasePath, token]);

  const eventRegistrationHref = useMemo(() => {
    if (!linkedEventId) return listBasePath;
    const target = `#/eventos/id/${encodeURIComponent(linkedEventId)}/inscricao`;
    return token ? target : buildLoginHref(target);
  }, [linkedEventId, listBasePath, token]);
  const explicitFixedPairRegistration =
    isFixedPairMode(challengeLink?.registrationType) ||
    isFixedPairMode(myRegistration?.registrationType) ||
    isFixedPairMode(myRegistration?.participantMode);
  const explicitIndividualRegistration =
    isIndividualMode(challengeLink?.registrationType) ||
    isIndividualMode(myRegistration?.registrationType) ||
    isIndividualMode(myRegistration?.participantMode);
  const isSuper8Registration =
    String(displayedChallenge?.challengeType || "")
      .trim()
      .toLowerCase() === "super8";
  const isFixedPairRegistration =
    !isSuper8Registration &&
    (String(displayedChallenge?.challengeType || "")
      .trim()
      .toLowerCase() === "poco" ||
      explicitFixedPairRegistration ||
      (!explicitIndividualRegistration &&
        inferFixedPairFromChallenge(displayedChallenge)));

  useEffect(() => {
    if (!linkedEventId) {
      setChallengeLink(null);
      return;
    }
    let active = true;
    getEventChallengeLink(linkedEventId)
      .then((data) => {
        if (!active) return;
        setChallengeLink(data);
      })
      .catch(() => {
        if (!active) return;
        setChallengeLink(null);
      });
    return () => {
      active = false;
    };
  }, [linkedEventId]);

  useEffect(() => {
    if (!token || !linkedEventId) {
      setMyRegistration(null);
      return;
    }
    let active = true;
    getMyEventRegistration(token, linkedEventId)
      .then((data) => {
        if (!active) return;
        setMyRegistration(data);
      })
      .catch(() => {
        if (!active) return;
        setMyRegistration(null);
      });
    return () => {
      active = false;
    };
  }, [linkedEventId, token]);

  const hasDisplayedChallenge = Boolean(displayedChallenge);
  const isTournamentChallenge =
    String(displayedChallenge?.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";
  const isPocoChallenge =
    String(displayedChallenge?.challengeType || "")
      .trim()
      .toLowerCase() === "poco";
  const hasRegistrationCategories =
    isTournamentChallenge ||
    ((isPocoChallenge ||
      String(displayedChallenge?.challengeType || "")
        .trim()
        .toLowerCase() === "challenge") &&
      Array.isArray(displayedChallenge?.registrationCategories) &&
      displayedChallenge.registrationCategories.length > 0);
  const activeChallengeRoundsCategoryKey = useMemo(
    () =>
      String(
        selectedTournamentCategoryKey ||
          myRegistration?.registrationCategoryKey ||
          displayedChallenge?.registrationCategories?.[0]?.categoryKey ||
          "",
      )
        .trim()
        .toLowerCase(),
    [
      displayedChallenge?.registrationCategories,
      myRegistration?.registrationCategoryKey,
      selectedTournamentCategoryKey,
    ],
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!hasDisplayedChallenge) {
      document.body.removeAttribute("data-challenge-detail-view");
      return;
    }
    document.body.setAttribute(
      "data-challenge-detail-view",
      isTournamentChallenge ? "tournament" : "challenge",
    );
    return () => {
      document.body.removeAttribute("data-challenge-detail-view");
    };
  }, [hasDisplayedChallenge, isTournamentChallenge]);

  useEffect(() => {
    if (!hasDisplayedChallenge) return;
    if (isTournamentChallenge) {
      setActiveTab("registrations");
    } else if (challengeStatusMeta.tone === "pending") {
      setActiveTab("registrations");
    } else if (
      challengeStatusMeta.tone === "started" ||
      challengeStatusMeta.tone === "finished"
    ) {
      setActiveTab("ranking");
    } else {
      setActiveTab("registrations");
    }
  }, [
    challengeId,
    challengeStatusMeta.tone,
    hasDisplayedChallenge,
    isTournamentChallenge,
  ]);

  useEffect(() => {
    if (!isTournamentChallenge || !hasDisplayedChallenge) {
      setTournamentEventSchedule(null);
      return;
    }
    let active = true;
    getPublicTournamentEventSchedule(challengeId, token || undefined)
      .then((schedule) => {
        if (active) setTournamentEventSchedule(schedule);
      })
      .catch(() => {
        if (active) setTournamentEventSchedule(null);
      });
    return () => {
      active = false;
    };
  }, [challengeId, hasDisplayedChallenge, isTournamentChallenge, token]);

  const hasPublicTournamentEventSchedule = Boolean(
    tournamentEventSchedule?.isPublicEnabled &&
      (tournamentEventSchedule.days || []).some(
        (day) =>
          day.isActive && (day.activities || []).some((activity) => activity.isActive),
      ),
  );

  useEffect(() => {
    if (activeTab === "eventSchedule" && !hasPublicTournamentEventSchedule) {
      setActiveTab("registrations");
    }
  }, [activeTab, hasPublicTournamentEventSchedule]);

  const participantsCount = Number(
    displayedChallenge?.participantsCount ||
      displayedChallenge?.participantCpfs?.length ||
      0,
  );
  const registrationsActiveCount = Number(
    (displayedChallenge?.registrations || []).filter(isActivePublicRegistration)
      .length || 0,
  );
  const registrationsActiveParticipantCount = Number(
    (displayedChallenge?.registrations || [])
      .filter(isActivePublicRegistration)
      .reduce((total, item) => total + registrationParticipantCount(item), 0) ||
      0,
  );
  const hasLinkedChallengeEvent = Boolean(
    challengeLink?.eventId ||
    displayedChallenge?.eventId ||
    (hasRegistrationCategories &&
      Array.isArray(displayedChallenge?.registrationCategories) &&
      displayedChallenge.registrationCategories.length > 0),
  );
  const hasRegistrationsSnapshot =
    Number(displayedChallenge?.registrations?.length || 0) > 0;
  const participantsCountBase = isTournamentChallenge
    ? hasLinkedChallengeEvent
      ? registrationsActiveParticipantCount
      : registrationsActiveCount > 0 || hasRegistrationsSnapshot
        ? registrationsActiveParticipantCount
        : participantsCount
    : hasLinkedChallengeEvent
      ? isFixedPairRegistration
        ? registrationsActiveCount * 2
        : registrationsActiveCount
      : registrationsActiveCount > 0 || hasRegistrationsSnapshot
        ? isFixedPairRegistration
          ? registrationsActiveCount * 2
          : registrationsActiveCount
        : participantsCount;
  const slotsLimit = displayedChallenge
    ? minimumParticipantsByType(displayedChallenge)
    : 0;
  const slotsRemaining = Math.max(0, slotsLimit - participantsCountBase);

  const participantsRows = useMemo<RegistrationRow[]>(() => {
    if (!displayedChallenge) return [];
    const registrationsRaw = displayedChallenge.registrations || [];
    const registrations = registrationsRaw
      .filter(isActivePublicRegistration)
      .sort(comparePublicRegistrationsByPaymentOrder);
    if (hasLinkedChallengeEvent || registrationsRaw.length > 0) {
      const rows = registrations.map((registration, index) => {
        const label =
          String(registration.name || "").trim() ||
          challengeDetailTexts.labels.playerFallback;
        const player1Name =
          String(registration.player1Name || "").trim() ||
          splitPairLabel(label)[0] ||
          label;
        const player2Name =
          String(registration.player2Name || "").trim() ||
          splitPairLabel(label)[1] ||
          "";
        const avatars = [
          {
            avatarUrl:
              String(registration.player1AvatarUrl || "").trim() || null,
            initials: initialsFromName(player1Name),
            name: player1Name,
            publicPlayerId: registration.player1PublicPlayerId || null,
            sex: normalizeAvatarSex(registration.player1Sex),
          },
          ...(player2Name
            ? [
                {
                  avatarUrl:
                    String(registration.player2AvatarUrl || "").trim() || null,
                  initials: initialsFromName(player2Name),
                  name: player2Name,
                  publicPlayerId: registration.player2PublicPlayerId || null,
                  sex: normalizeAvatarSex(registration.player2Sex),
                },
              ]
            : []),
        ];
        const registrationCategoryKey = String(
          registration.registrationCategoryKey || "",
        )
          .trim()
          .toLowerCase();
        const registrationCategoryName = String(
          registration.registrationCategoryName || "",
        ).trim();
        return {
          key: `registration-${registration.id}-${index}`,
          position: index + 1,
          name: label,
          avatars,
          statusLabel: resolveRegistrationStatusLabel({
            registrationStatus: registration.registrationStatus,
            paymentStatus: registration.paymentStatus,
            paymentRequired: registration.paymentRequired,
          }),
          statusTone: resolveRegistrationStatusTone({
            registrationStatus: registration.registrationStatus,
            paymentStatus: registration.paymentStatus,
            paymentRequired: registration.paymentRequired,
          }),
          participantCount: registrationParticipantCount(registration),
          paymentUrl: String(registration.paymentUrl || "").trim() || null,
          registrationCategoryKey: registrationCategoryKey || null,
          registrationCategoryName: registrationCategoryName || null,
        };
      });
      return rows;
    }
    const participants = displayedChallenge.participants || [];
    if (isFixedPairRegistration) {
      return (displayedChallenge.pairs || []).map((pair, index) => {
        const label =
          String(pair.label || "").trim() ||
          [String(pair.nome1 || "").trim(), String(pair.nome2 || "").trim()]
            .filter(Boolean)
            .join(" / ") ||
          challengeDetailTexts.labels.playerFallback;
        const initials = `${String(pair.nome1 || "")
          .trim()
          .charAt(0)}${String(pair.nome2 || "")
          .trim()
          .charAt(0)}`.toUpperCase();
        const names = splitPairLabel(label);
        const player1Name =
          String(pair.nome1 || "").trim() || names[0] || label;
        const player2Name = String(pair.nome2 || "").trim() || names[1] || "";
        return {
          key: `${pair.id}-${index}`,
          position: index + 1,
          name: label,
          avatars: [
            {
              avatarUrl: null,
              initials: initialsFromName(player1Name) || initials.slice(0, 1),
              name: player1Name,
              publicPlayerId: pair.player1PublicPlayerId || null,
              sex: normalizeAvatarSex(displayedChallenge.categoryGender),
            },
            ...(player2Name
              ? [
                  {
                    avatarUrl: null,
                    initials:
                      initialsFromName(player2Name) || initials.slice(1, 2),
                    name: player2Name,
                    publicPlayerId: pair.player2PublicPlayerId || null,
                    sex: normalizeAvatarSex(displayedChallenge.categoryGender),
                  },
                ]
              : []),
          ],
          statusLabel: challengeDetailTexts.registration.statuses.confirmed,
          statusTone: "confirmed" as RegistrationStatusTone,
          participantCount: 2,
          paymentUrl: null,
          registrationCategoryKey: null,
          registrationCategoryName: null,
        };
      });
    }

    return participants.map((participant, index) => {
      const label =
        String(participant.name || "").trim() ||
        challengeDetailTexts.labels.playerFallback;
      const initials = label
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0]?.toUpperCase() || "")
        .join("");
      return {
        key: `${participant.cpf}-${index}`,
        position: index + 1,
        name: label,
        avatars: [
          {
            avatarUrl: null,
            initials:
              initials ||
              challengeDetailTexts.labels.playerFallback
                .slice(0, 2)
                .toUpperCase(),
            name: label,
            publicPlayerId: participant.publicPlayerId || null,
            sex: normalizeAvatarSex(displayedChallenge.categoryGender),
          },
        ],
        statusLabel: challengeDetailTexts.registration.statuses.confirmed,
        statusTone: "confirmed" as RegistrationStatusTone,
        participantCount: 1,
        paymentUrl: null,
        registrationCategoryKey: null,
        registrationCategoryName: null,
      };
    });
  }, [displayedChallenge, hasLinkedChallengeEvent, isFixedPairRegistration]);

  const activeChallengeRoundsDetailed = useMemo(() => {
    const rounds = displayedChallenge?.roundsDetailed || [];
    if (
      isTournamentChallenge ||
      !hasRegistrationCategories ||
      !activeChallengeRoundsCategoryKey
    ) {
      return rounds;
    }
    return selectCategoryRounds(rounds, activeChallengeRoundsCategoryKey, displayedChallenge?.registrationCategories?.length || 0);
  }, [
    activeChallengeRoundsCategoryKey,
    displayedChallenge?.roundsDetailed,
    displayedChallenge?.registrationCategories?.length,
    hasRegistrationCategories,
    isTournamentChallenge,
  ]);
  const flattenedMatches = useMemo(
    () => activeChallengeRoundsDetailed.flat(),
    [activeChallengeRoundsDetailed],
  );
  const activeChallengePairs = useMemo(() => {
    if (!displayedChallenge) return [];
    if (isTournamentChallenge || !hasRegistrationCategories) return displayedChallenge.pairs || [];
    return selectCategoryPairs(displayedChallenge, activeChallengeRoundsDetailed, activeChallengeRoundsCategoryKey);
  }, [displayedChallenge, isTournamentChallenge, hasRegistrationCategories, activeChallengeRoundsDetailed, activeChallengeRoundsCategoryKey]);
  const pairLabelById = useMemo(
    () =>
      (displayedChallenge?.pairs || []).reduce<Record<string, string>>(
        (acc, pair) => {
          const pairId = normalizePairId(pair.id);
          if (pairId) acc[pairId] = String(pair.label || "").trim();
          return acc;
        },
        {},
      ),
    [displayedChallenge?.pairs],
  );
  const participantAvatarByCpf = useMemo(() => {
    const avatars = new Map<string, ChallengeAvatarItem>();
    const fallbackSex = normalizeAvatarSex(displayedChallenge?.categoryGender);

    (displayedChallenge?.participants || []).forEach((participant) => {
      const cpf = normalizeCpfDigits(participant.cpf);
      if (!cpf) return;
      avatars.set(cpf, {
        avatarUrl: String(participant.avatarUrl || "").trim(),
        name:
          String(participant.name || "").trim() ||
          challengeDetailTexts.labels.playerFallback,
        sex: normalizeAvatarSex(participant.sex) || fallbackSex,
        publicPlayerId: participant.publicPlayerId || null,
        profileHref: publicPlayerHref(participant.publicPlayerId),
      });
    });

    (displayedChallenge?.registrations || []).forEach((registration) => {
      const player1Cpf = normalizeCpfDigits(registration.player1Cpf);
      if (player1Cpf) {
        avatars.set(player1Cpf, {
          avatarUrl: String(registration.player1AvatarUrl || "").trim(),
          name:
            String(registration.player1Name || "").trim() ||
            challengeDetailTexts.labels.playerFallback,
          sex: normalizeAvatarSex(registration.player1Sex) || fallbackSex,
          publicPlayerId: registration.player1PublicPlayerId || null,
          profileHref: publicPlayerHref(registration.player1PublicPlayerId),
        });
      }
      const player2Cpf = normalizeCpfDigits(registration.player2Cpf);
      if (player2Cpf) {
        avatars.set(player2Cpf, {
          avatarUrl: String(registration.player2AvatarUrl || "").trim(),
          name:
            String(registration.player2Name || "").trim() ||
            challengeDetailTexts.labels.playerFallback,
          sex: normalizeAvatarSex(registration.player2Sex) || fallbackSex,
          publicPlayerId: registration.player2PublicPlayerId || null,
          profileHref: publicPlayerHref(registration.player2PublicPlayerId),
        });
      }
    });

    return avatars;
  }, [displayedChallenge]);
  const pairAvatarsById = useMemo(() => {
    return (displayedChallenge?.pairs || []).reduce<
      Record<string, ChallengeAvatarItem[]>
    >((acc, pair) => {
      const pairId = normalizePairId(pair.id);
      if (!pairId) return acc;
      const fallbackSex = normalizeAvatarSex(
        displayedChallenge?.categoryGender,
      );
      const members = [
        {
          cpf: pair.cpf1,
          name: pair.nome1,
          avatarUrl: pair.player1AvatarUrl,
          sex: pair.player1Sex,
        },
        {
          cpf: pair.cpf2,
          name: pair.nome2,
          avatarUrl: pair.player2AvatarUrl,
          sex: pair.player2Sex,
        },
      ]
        .filter((member) => String(member.name || member.cpf || "").trim())
        .map((member) => {
          const participantAvatar = participantAvatarByCpf.get(
            normalizeCpfDigits(member.cpf),
          );
          return {
            avatarUrl:
              String(member.avatarUrl || "").trim() ||
              participantAvatar?.avatarUrl ||
              "",
            name:
              String(member.name || "").trim() ||
              participantAvatar?.name ||
              challengeDetailTexts.labels.playerFallback,
            sex:
              normalizeAvatarSex(member.sex) ||
              participantAvatar?.sex ||
              fallbackSex,
            publicPlayerId:
              member.cpf === pair.cpf1
                ? pair.player1PublicPlayerId || participantAvatar?.publicPlayerId || null
                : pair.player2PublicPlayerId || participantAvatar?.publicPlayerId || null,
            profileHref:
              member.cpf === pair.cpf1
                ? publicPlayerHref(pair.player1PublicPlayerId) ||
                  participantAvatar?.profileHref
                : publicPlayerHref(pair.player2PublicPlayerId) ||
                  participantAvatar?.profileHref,
          };
        });
      acc[pairId] = members;
      return acc;
    }, {});
  }, [displayedChallenge, participantAvatarByCpf]);

  const rankingRowsData = useMemo(() => {
    if (!displayedChallenge) return [];
    if (displayedChallenge.challengeType === "super8") {
      return calculateSuper8Ranking(
        displayedChallenge.participants || [],
        activeChallengePairs,
        flattenedMatches,
      );
    }
    if (displayedChallenge.challengeType === "poco") {
      return calculatePocoRanking(
        activeChallengePairs,
        activeChallengeRoundsDetailed,
      );
    }
    return calculateRanking(activeChallengePairs, flattenedMatches);
  }, [
    activeChallengePairs,
    activeChallengeRoundsDetailed,
    displayedChallenge,
    flattenedMatches,
  ]);
  const pocoPointsByPairId = useMemo(() => {
    if (displayedChallenge?.challengeType !== "poco") {
      return {} as Record<string, number>;
    }
    return rankingRowsData.reduce<Record<string, number>>((acc, row) => {
      acc[normalizePairId(row.id)] = row.points;
      return acc;
    }, {});
  }, [displayedChallenge?.challengeType, rankingRowsData]);

  const maxMatches = useMemo(
    () =>
      rankingRowsData.length
        ? Math.max(...rankingRowsData.map((item) => item.matches))
        : 0,
    [rankingRowsData],
  );
  const pocoRoundCount = useMemo(
    () =>
      displayedChallenge?.challengeType === "poco"
        ? getPocoRankingRoundCount(activeChallengeRoundsDetailed)
        : 0,
    [activeChallengeRoundsDetailed, displayedChallenge?.challengeType],
  );

  const rankingColumns = useMemo<
    Array<ResponsiveRankingColumn<RankingRow>>
  >(() => {
    const winsColumn: ResponsiveRankingColumn<RankingRow> = {
      id: "wins",
      header: challengeDetailTexts.ranking.columns.wins,
      mobileLabel: challengeDetailTexts.ranking.columns.winsShort,
      render: (row) => row.wins,
    };
    const base: Array<ResponsiveRankingColumn<RankingRow>> = [
      {
        id: "balance",
        header: challengeDetailTexts.ranking.columns.balance,
        mobileLabel: challengeDetailTexts.ranking.columns.balanceShort,
        render: (row) => row.gamesBalance,
      },
      {
        id: "matches",
        header: challengeDetailTexts.ranking.columns.matches,
        mobileLabel: challengeDetailTexts.ranking.columns.matchesShort,
        render: (row) => row.matches,
      },
      {
        id: "gamesWon",
        header: challengeDetailTexts.ranking.columns.gamesWon,
        mobileLabel: challengeDetailTexts.ranking.columns.gamesWonShort,
        render: (row) => row.gamesWon,
      },
      {
        id: "gamesLost",
        header: challengeDetailTexts.ranking.columns.gamesLost,
        mobileLabel: challengeDetailTexts.ranking.columns.gamesLostShort,
        render: (row) => row.gamesLost,
      },
      winsColumn,
      {
        id: "losses",
        header: challengeDetailTexts.ranking.columns.losses,
        mobileLabel: challengeDetailTexts.ranking.columns.lossesShort,
        render: (row) => row.losses,
      },
    ];
    if (displayedChallenge?.challengeType === "super8") {
      return [winsColumn, ...base.filter((column) => column.id !== "wins")];
    }
    if (displayedChallenge?.challengeType !== "poco") return base;
    return buildPocoRankingColumns<RankingRow>(pocoRoundCount, {
      total: challengeDetailTexts.ranking.columns.total,
      final: challengeDetailTexts.ranking.columns.final,
      roundShort: challengeDetailTexts.ranking.columns.roundShort,
    });
  }, [displayedChallenge?.challengeType, pocoRoundCount]);

  const rankingRows = useMemo<Array<ResponsiveRankingRow<RankingRow>>>(
    () =>
      rankingRowsData.map((item, index) => {
        const mobileSummaryKind: MobileRankingSummaryKind =
          displayedChallenge?.challengeType === "poco"
            ? "points"
            : displayedChallenge?.challengeType === "super8"
              ? "wins"
              : "balance";
        const stateClasses = [
          index === 0 ? "is-leader" : "",
          displayedChallenge?.challengeType !== "poco" &&
          item.matches < maxMatches
            ? "has-less-matches"
            : "",
        ]
          .filter(Boolean)
          .join(" ");
        return {
          id: item.id,
          rank: index + 1,
          title: item.label,
          avatars:
            displayedChallenge?.challengeType === "super8"
              ? [
                  participantAvatarByCpf.get(normalizeCpfDigits(item.id)),
                ].filter((avatar): avatar is ChallengeAvatarItem =>
                  Boolean(avatar),
                )
              : pairAvatarsById[normalizePairId(item.id)] || [],
          profileHref:
            displayedChallenge?.challengeType === "super8"
              ? participantAvatarByCpf.get(normalizeCpfDigits(item.id))
                  ?.profileHref
              : undefined,
          data: item,
          summaryValue: renderChallengeRankingMobileValue(
            item,
            mobileSummaryKind,
          ),
          rowClassName: stateClasses,
          mobileItemClassName: stateClasses,
        };
      }),
    [
      displayedChallenge?.challengeType,
      maxMatches,
      pairAvatarsById,
      participantAvatarByCpf,
      rankingRowsData,
    ],
  );

  const challengeCategory = displayedChallenge
    ? resolveChallengeCategory(displayedChallenge)
    : challengeDetailTexts.labels.toBeDefined;

  const amountPerPlayerLabel = formatCurrency(
    challengeLink?.amountPerPlayerCents ?? null,
  );
  const tournamentCategoryByKey = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<ChallengeDetail["registrationCategories"]>[number]
    >();
    (displayedChallenge?.registrationCategories || []).forEach((category) => {
      const key = String(category.categoryKey || "")
        .trim()
        .toLowerCase();
      if (key) map.set(key, category);
    });
    return map;
  }, [displayedChallenge?.registrationCategories]);
  const tournamentHasOnlyFixedPairCategories =
    isTournamentChallenge &&
    tournamentCategoryByKey.size > 0 &&
    Array.from(tournamentCategoryByKey.values()).every((category) =>
      isFixedPairMode(category.registrationType),
    );
  const displayAsFixedPair =
    !isSuper8Registration &&
    (isFixedPairRegistration || tournamentHasOnlyFixedPairCategories);
  const slotsLimitForDisplay =
    displayAsFixedPair && !isTournamentChallenge
      ? fixedPairDisplayCount(slotsLimit)
      : displayAsFixedPair
        ? fixedPairDisplayCount(slotsLimit)
        : slotsLimit;
  const participantsCountForDisplay = displayAsFixedPair
    ? fixedPairDisplayCount(participantsCountBase)
    : participantsCountBase;
  const slotsRemainingForDisplay = displayAsFixedPair
    ? fixedPairDisplayCount(slotsRemaining)
    : slotsRemaining;
  const slotsDisplayUnit = displayAsFixedPair
    ? challengeDetailTexts.mode.pairs
    : challengeDetailTexts.mode.players;
  const registrationOpen = challengeStatusMeta.tone === "pending";
  const hasEventLinked = Boolean(challengeLink);
  const hasRegistration =
    Boolean(myRegistration) && myRegistration?.status !== "canceled";
  const registrationsOpenByAdmin = challengeLink
    ? Boolean(challengeLink.registrationsOpen)
    : true;
  const waitlistEnabled = false;
  const hasGeneratedTournamentBrackets =
    isTournamentChallenge && publishedTournamentBracketMatchesCount > 0;

  const ctaState = useMemo(() => {
    if (challengeStatusMeta.tone === "finished") {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.finishedTitle,
        text: challengeDetailTexts.cta.finishedText,
        buttonLabel: "",
        href: "",
        targetTab: null,
        disabled: true,
        footer: challengeDetailTexts.cta.finishedFooter,
        icon: "workspace_premium",
        variant: "finished",
      };
    }

    if (!hasEventLinked) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.pendingTitle,
        text: isFixedPairRegistration
          ? challengeDetailTexts.cta.pendingTextFixedPair
          : challengeDetailTexts.cta.pendingTextSingle,
        buttonLabel: challengeDetailTexts.cta.eventUnavailable,
        href: listBasePath,
        targetTab: null,
        disabled: true,
        footer: challengeDetailTexts.cta.eventUnavailableFooter,
        icon: "event",
        variant: "pending",
      };
    }

    if (hasGeneratedTournamentBrackets) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.bracketsGeneratedTitle,
        text: challengeDetailTexts.cta.bracketsGeneratedText,
        buttonLabel: challengeDetailTexts.cta.bracketsGeneratedButton,
        href: "",
        targetTab: "brackets" as DetailTab,
        disabled: false,
        footer: challengeDetailTexts.cta.bracketsGeneratedFooter,
        icon: "account_tree",
        variant: "brackets",
      };
    }

    if (!registrationOpen) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.registrationClosedTitle,
        text: challengeDetailTexts.cta.registrationClosedText,
        buttonLabel: challengeDetailTexts.cta.registrationClosedButton,
        href: eventDetailsHref,
        targetTab: null,
        disabled: true,
        footer: challengeDetailTexts.cta.registrationClosedFooter,
        icon: "event_busy",
        variant: "closed",
      };
    }

    if (!hasRegistration && !registrationsOpenByAdmin) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.registrationClosedTitle,
        text: challengeDetailTexts.cta.registrationClosedText,
        buttonLabel: challengeDetailTexts.cta.registrationClosedButton,
        href: eventDetailsHref,
        targetTab: null,
        disabled: true,
        footer: challengeDetailTexts.cta.registrationClosedFooter,
        icon: "event_busy",
        variant: "closed",
      };
    }

    if (
      canRegisterInAnotherCategory({
        hasRegistration,
        challengeType: displayedChallenge?.challengeType,
        registrationsOpen: registrationsOpenByAdmin,
        categoryCount: tournamentCategoryByKey.size,
      })
    ) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.pendingTitle,
        text: challengeDetailTexts.cta.registrationStartedText,
        buttonLabel: challengeDetailTexts.cta.registerAnotherCategory,
        href: eventRegistrationHref,
        targetTab: null,
        disabled: false,
        footer: `${challengeDetailTexts.cta.availableSlotsPrefix} ${slotsRemainingForDisplay} ${slotsDisplayUnit}`,
        icon: "event",
        variant: "registered",
      };
    }

    if (hasRegistration) {
      const isConfirmed = myRegistration?.status === "confirmed";
      return {
        visible: true,
        showInfo: true,
        title: isFixedPairRegistration
          ? challengeDetailTexts.cta.registrationStartedTitleFixedPair
          : challengeDetailTexts.cta.registrationStartedTitleSingle,
        text: challengeDetailTexts.cta.registrationStartedText,
        buttonLabel: isConfirmed
          ? challengeDetailTexts.cta.viewMyRegistration
          : challengeDetailTexts.cta.followRegistration,
        href: eventRegistrationHref,
        targetTab: null,
        disabled: false,
        footer:
          myRegistration?.status === "waitlist"
            ? challengeDetailTexts.cta.waitlistMe
            : `${challengeDetailTexts.cta.availableSlotsPrefix} ${slotsRemainingForDisplay} ${slotsDisplayUnit}`,
        icon: "event",
        variant: "registered",
      };
    }

    if (slotsRemaining > 0) {
      return {
        visible: true,
        showInfo: false,
        title: challengeDetailTexts.cta.pendingTitle,
        text: isFixedPairRegistration
          ? challengeDetailTexts.cta.pendingTextFixedPair
          : challengeDetailTexts.cta.pendingTextSingle,
        buttonLabel: isFixedPairRegistration
          ? challengeDetailTexts.cta.goToEventFixedPair
          : challengeDetailTexts.cta.goToEventSingle,
        href: isTournamentChallenge ? eventRegistrationHref : eventDetailsHref,
        targetTab: null,
        disabled: false,
        footer: `${challengeDetailTexts.cta.availableSlotsPrefix} ${slotsRemainingForDisplay} ${slotsDisplayUnit}`,
        icon: "event",
        variant: "",
      };
    }

    if (waitlistEnabled) {
      return {
        visible: true,
        showInfo: true,
        title: challengeDetailTexts.cta.soldOutTitle,
        text: isFixedPairRegistration
          ? challengeDetailTexts.cta.waitlistTextFixedPair
          : challengeDetailTexts.cta.waitlistTextSingle,
        buttonLabel: isFixedPairRegistration
          ? challengeDetailTexts.cta.waitlistButtonFixedPair
          : challengeDetailTexts.cta.waitlistButtonSingle,
        href: eventDetailsHref,
        targetTab: null,
        disabled: false,
        footer: challengeDetailTexts.cta.waitlistFooter,
        icon: "error",
        variant: "soldout",
      };
    }

    return {
      visible: true,
      showInfo: true,
      title: challengeDetailTexts.cta.soldOutTitle,
      text: challengeDetailTexts.cta.soldOutText,
      buttonLabel: challengeDetailTexts.cta.soldOutButton,
      href: eventDetailsHref,
      targetTab: null,
      disabled: true,
      footer: challengeDetailTexts.cta.soldOutFooter,
      icon: "error",
      variant: "soldout",
    };
  }, [
    challengeStatusMeta.tone,
    eventDetailsHref,
    eventRegistrationHref,
    hasGeneratedTournamentBrackets,
    hasEventLinked,
    hasRegistration,
    registrationsOpenByAdmin,
    myRegistration?.status,
    registrationOpen,
    slotsRemaining,
    slotsRemainingForDisplay,
    slotsDisplayUnit,
    waitlistEnabled,
    isFixedPairRegistration,
    displayedChallenge?.challengeType,
    isTournamentChallenge,
    listBasePath,
    tournamentCategoryByKey,
  ]);
  const mobileCtaButtonLabel =
    !ctaState.disabled &&
    !hasRegistration &&
    slotsRemaining > 0 &&
    !ctaState.targetTab &&
    ctaState.variant !== "brackets"
      ? challengeDetailTexts.cta.mobileRegister
      : ctaState.buttonLabel;

  const tournamentRegistrationCategoryOptions = useMemo(() => {
    if (!hasRegistrationCategories)
      return [] as Array<{ key: string; name: string; timeLabel: string }>;
    const map = new Map<string, { name: string; timeLabel: string }>();
    const categories = sortRegistrationCategories(
      Array.isArray(displayedChallenge?.registrationCategories)
        ? displayedChallenge.registrationCategories
        : [],
    );
    categories.forEach((category, index) => {
      const key = String(category.categoryKey || `categoria-${index + 1}`)
        .trim()
        .toLowerCase();
      if (!key) return;
      const scheduledAt = String(category.scheduledAt || "").trim();
      map.set(key, {
        name:
          String(category.categoryName || "").trim() ||
          `${challengeDetailTexts.labels.categoryFallback} ${index + 1}`,
        timeLabel: scheduledAt ? myGameDateParts(scheduledAt).timeLabel : "",
      });
    });
    return Array.from(map.entries()).map(([key, value]) => ({
      key,
      ...value,
    }));
  }, [displayedChallenge?.registrationCategories, hasRegistrationCategories]);

  useEffect(() => {
    if (
      !isTournamentChallenge ||
      tournamentRegistrationCategoryOptions.length === 0
    ) {
      setPublishedTournamentBracketMatchesCount(0);
      return;
    }
    let active = true;
    Promise.all(
      tournamentRegistrationCategoryOptions.map((category) =>
        getPublicTournamentBracket(
          challengeId,
          category.key,
          token || undefined,
        )
          .then((bracket) =>
            (bracket.rounds || []).reduce(
              (total, round) => total + (round.matches || []).length,
              0,
            ),
          )
          .catch(() => 0),
      ),
    ).then((counts) => {
      if (!active) return;
      setPublishedTournamentBracketMatchesCount(
        counts.reduce((total, count) => total + count, 0),
      );
    });
    return () => {
      active = false;
    };
  }, [
    challengeId,
    isTournamentChallenge,
    token,
    tournamentRegistrationCategoryOptions,
  ]);

  useEffect(() => {
    if (!hasRegistrationCategories) {
      setSelectedTournamentCategoryKey("");
      return;
    }
    setSelectedTournamentCategoryKey((current) => {
      if (!current) return "";
      if (
        tournamentRegistrationCategoryOptions.some(
          (item) => item.key === current,
        )
      ) {
        return current;
      }
      return "";
    });
  }, [hasRegistrationCategories, tournamentRegistrationCategoryOptions]);

  const registrationTournamentCategoryKey = useMemo(
    () => String(selectedTournamentCategoryKey || "").trim().toLowerCase(),
    [selectedTournamentCategoryKey],
  );

  const activeTournamentCategoryKey = useMemo(
    () =>
      String(
        selectedTournamentCategoryKey ||
          tournamentRegistrationCategoryOptions[0]?.key ||
          "",
      )
        .trim()
        .toLowerCase(),
    [selectedTournamentCategoryKey, tournamentRegistrationCategoryOptions],
  );
  const registrationCategorySelectOptions = useMemo(
    () => [
      {
        key: "",
        label: siteTexts.challenges.publicList.filters.all,
      },
      ...tournamentRegistrationCategoryOptions.map((category) => ({
        key: category.key,
        label: category.timeLabel
          ? `${category.name} - ${category.timeLabel}`
          : category.name,
      })),
    ],
    [tournamentRegistrationCategoryOptions],
  );
  const bracketCategorySelectOptions = useMemo(
    () =>
      tournamentRegistrationCategoryOptions.map((category) => ({
        key: category.key,
        label: category.timeLabel
          ? `${category.name} - ${category.timeLabel}`
          : category.name,
      })),
    [tournamentRegistrationCategoryOptions],
  );

  const filteredParticipantsRows = useMemo(() => {
    if (!hasRegistrationCategories) return participantsRows;
    const selected = registrationTournamentCategoryKey;
    if (!selected) return participantsRows;
    return participantsRows.filter(
      (row) =>
        String(row.registrationCategoryKey || "")
          .trim()
          .toLowerCase() === selected,
    );
  }, [
    hasRegistrationCategories,
    participantsRows,
    registrationTournamentCategoryKey,
  ]);

  const tournamentRegistrationCounters = useMemo(() => {
    if (!hasRegistrationCategories) return null;
    const categories = Array.isArray(displayedChallenge?.registrationCategories)
      ? displayedChallenge.registrationCategories
      : [];
    const byCategory = new Map<
      string,
      {
        registrations: number;
        registrationUnits: number;
        slots: number;
        slotUnits: number;
      }
    >();
    let totalSlots = 0;
    let totalSlotUnits = 0;
    categories.forEach((item, index) => {
      const key = String(item.categoryKey || `categoria-${index + 1}`)
        .trim()
        .toLowerCase();
      if (!key) return;
      const isFixedPairCategory = isFixedPairMode(item.registrationType);
      const configuredSlots = Math.max(
        0,
        Math.floor(Number(item.slotLimit || 0)),
      );
      const slotLimit = isFixedPairCategory
        ? configuredSlots * 2
        : configuredSlots;
      const slotUnits = registrationUnitCount(slotLimit, isFixedPairCategory);
      totalSlots += slotLimit;
      totalSlotUnits += slotUnits;
      byCategory.set(key, {
        registrations: 0,
        registrationUnits: 0,
        slots: slotLimit,
        slotUnits,
      });
    });

    participantsRows.forEach((row) => {
      const key = String(row.registrationCategoryKey || "")
        .trim()
        .toLowerCase();
      if (!key) return;
      const category = tournamentCategoryByKey.get(key);
      const isFixedPairCategory =
        isFixedPairMode(category?.registrationType) ||
        (!category && isFixedPairRegistration);
      const current = byCategory.get(key) || {
        registrations: 0,
        registrationUnits: 0,
        slots: 0,
        slotUnits: 0,
      };
      const nextRegistrations = current.registrations + row.participantCount;
      byCategory.set(key, {
        registrations: nextRegistrations,
        registrationUnits: registrationUnitCount(
          nextRegistrations,
          isFixedPairCategory,
        ),
        slots: current.slots,
        slotUnits: current.slotUnits,
      });
    });

    const totalRegistrations = participantsRows.reduce(
      (total, row) => total + row.participantCount,
      0,
    );
    const totalRegistrationUnits =
      categories.length > 0
        ? Array.from(byCategory.values()).reduce(
            (total, row) => total + row.registrationUnits,
            0,
          )
        : registrationUnitCount(totalRegistrations, isFixedPairRegistration);

    return {
      totalRegistrations,
      totalRegistrationUnits,
      totalSlots,
      totalSlotUnits,
      byCategory,
    };
  }, [
    displayedChallenge?.registrationCategories,
    hasRegistrationCategories,
    isFixedPairRegistration,
    participantsRows,
    tournamentCategoryByKey,
  ]);

  const registrationPanelParticipantsCount = hasRegistrationCategories
    ? registrationTournamentCategoryKey
      ? (() => {
          const selected = registrationTournamentCategoryKey;
          const selectedCounter =
            tournamentRegistrationCounters?.byCategory.get(selected);
          if (selectedCounter) return selectedCounter.registrationUnits;
          const category = tournamentCategoryByKey.get(selected);
          return registrationUnitCount(
            filteredParticipantsRows.reduce(
              (total, row) => total + row.participantCount,
              0,
            ),
            isFixedPairMode(category?.registrationType) ||
              isFixedPairRegistration,
          );
        })()
      : tournamentRegistrationCounters?.totalRegistrationUnits ||
        registrationUnitCount(
          tournamentRegistrationCounters?.totalRegistrations ||
            participantsRows.length,
          displayAsFixedPair,
        )
    : participantsCountForDisplay;

  const registrationPanelSlotsLimit = hasRegistrationCategories
    ? (() => {
        const selected = registrationTournamentCategoryKey;
        if (!selected) {
          const totalSlots = Number(
            tournamentRegistrationCounters?.totalSlotUnits || 0,
          );
          if (Number.isFinite(totalSlots) && totalSlots > 0) return totalSlots;
          return slotsLimitForDisplay;
        }
        const selectedSlots = Number(
          tournamentRegistrationCounters?.byCategory.get(selected)?.slotUnits ||
            0,
        );
        return Number.isFinite(selectedSlots) && selectedSlots > 0
          ? selectedSlots
          : 0;
      })()
    : slotsLimitForDisplay;
  const selectedTournamentCategory = registrationTournamentCategoryKey
    ? tournamentCategoryByKey.get(
        registrationTournamentCategoryKey,
      ) || null
    : null;
  const registrationPanelIsFixedPair =
    isTournamentChallenge && selectedTournamentCategory
      ? isFixedPairMode(selectedTournamentCategory.registrationType)
      : displayAsFixedPair;
  const summaryRegistrationsForDisplay =
    isTournamentChallenge &&
    hasRegistrationCategories &&
    Number(tournamentRegistrationCounters?.totalRegistrationUnits || 0) >= 0
      ? Number(tournamentRegistrationCounters?.totalRegistrationUnits || 0)
      : participantsCountForDisplay;
  const summarySlotsLimitForDisplay =
    isTournamentChallenge &&
    Number(tournamentRegistrationCounters?.totalSlotUnits || 0) > 0
      ? Number(tournamentRegistrationCounters?.totalSlotUnits || 0)
      : slotsLimitForDisplay;
  const summarySlotsRemainingForDisplay =
    isTournamentChallenge &&
    Number(tournamentRegistrationCounters?.totalSlotUnits || 0) > 0
      ? Math.max(
          0,
          Number(tournamentRegistrationCounters?.totalSlotUnits || 0) -
            Number(tournamentRegistrationCounters?.totalRegistrationUnits || 0),
        )
      : slotsRemainingForDisplay;
  const summarySlotsDisplayUnit =
    isTournamentChallenge && !displayAsFixedPair
      ? challengeDetailTexts.mode.players
      : slotsDisplayUnit;
  const myPairLabel =
    [
      String(user?.name || "").trim(),
      String(myRegistration?.teammateName || "").trim(),
    ]
      .filter(Boolean)
      .join(" / ") || challengeDetailTexts.myGames.yourPair;
  const hasActiveMyRegistration =
    Boolean(user && myRegistration) &&
    myRegistration?.status !== "canceled" &&
    myRegistration?.paymentStatus !== "canceled" &&
    myRegistration?.paymentStatus !== "refunded";
  useBodyScrollLock(myGamesOpen && hasActiveMyRegistration);

  useEffect(() => {
    if (!hasActiveMyRegistration || loading) return;
    const hash = String(window.location.hash || "");
    const queryIndex = hash.indexOf("?");
    if (queryIndex < 0) return;
    const section = new URLSearchParams(hash.slice(queryIndex + 1)).get("section");
    if (section === "my-games") setMyGamesOpen(true);
  }, [challengeId, hasActiveMyRegistration, loading]);

  useEffect(() => {
    if (
      !myGamesOpen ||
      !isTournamentChallenge ||
      !hasActiveMyRegistration ||
      !myRegistration?.id ||
      tournamentRegistrationCategoryOptions.length === 0
    ) {
      if (!myGamesOpen) setMyTournamentGamesError("");
      return;
    }
    let active = true;
    const registrationId = Number(myRegistration.id);
    const categoryNameByKey = new Map(
      tournamentRegistrationCategoryOptions.map((category) => [
        String(category.key || "")
          .trim()
          .toLowerCase(),
        category.name,
      ]),
    );
    const categoryKeyByName = new Map(
      tournamentRegistrationCategoryOptions.map((category) => [
        category.name,
        String(category.key || "")
          .trim()
          .toLowerCase(),
      ]),
    );

    const gameFromMatch = ({
      categoryKey,
      categoryName,
      match,
      slot,
      fallbackIndex,
    }: {
      categoryKey: string;
      categoryName: string;
      match: PublicTournamentBracketMatch;
      slot?: PublicTournamentScheduleSlot | null;
      fallbackIndex: number;
    }): MyGameItem | null => {
      const isSideA = Number(match.sideARegistrationId || 0) === registrationId;
      const isSideB = Number(match.sideBRegistrationId || 0) === registrationId;
      if (!isSideA && !isSideB) return null;
      const status = myGameStatusMeta(
        slot?.matchStatus || slot?.status || match.status,
      );
      const date = myGameDateParts(slot?.slotStartAt || null);
      return {
        id: `tournament-${categoryKey}-${match.id || fallbackIndex}`,
        categoryName,
        pairLabel: String(
          (isSideA ? match.sideALabel : match.sideBLabel) || myPairLabel,
        ).trim(),
        opponentLabel: String(
          (isSideA ? match.sideBLabel : match.sideALabel) || "A definir",
        ).trim(),
        phaseLabel:
          String(match.groupName || "").trim() ||
          myGameRoundLabel(match.roundCode, match.roundNumber),
        dateLabel: date.dateLabel,
        timeLabel: date.timeLabel,
        courtLabel: myGameCourtLabel(slot?.courtName),
        statusLabel: status.label,
        statusKey: status.key,
        timestamp: date.timestamp + fallbackIndex,
        isFinished: status.finished,
        hasSchedule: date.hasSchedule,
      };
    };

    setMyTournamentGamesLoading(true);
    setMyTournamentGamesError("");
    Promise.all([
      getPublicTournamentSchedule(challengeId, token || undefined).catch(
        () => null,
      ),
      Promise.all(
        tournamentRegistrationCategoryOptions.map((category) =>
          getPublicTournamentBracket(
            challengeId,
            category.key,
            token || undefined,
          )
            .then((bracket) => ({ category, bracket }))
            .catch(() => null),
        ),
      ),
    ])
      .then(([schedule, bracketResults]) => {
        if (!active) return;
        const gamesById = new Map<string, MyGameItem>();
        const slots = schedule?.slots || [];
        slots.forEach((slot, index) => {
          const categoryKey = String(slot.categoryKey || "")
            .trim()
            .toLowerCase();
          const categoryName =
            categoryNameByKey.get(categoryKey) ||
            String(slot.categoryKey || "").trim() ||
            challengeDetailTexts.labels.categoryFallback;
          const match = {
            id: Number(slot.matchId || slot.id || 0),
            stage: slot.stage || "",
            groupKey: slot.groupKey || null,
            groupName: slot.groupName || null,
            roundNumber: Number(slot.roundNumber || 0),
            roundCode: String(slot.roundCode || ""),
            matchNumber: Number(slot.matchNumber || 0),
            sideARegistrationId: slot.sideARegistrationId || null,
            sideBRegistrationId: slot.sideBRegistrationId || null,
            sideALabel: slot.sideALabel || null,
            sideBLabel: slot.sideBLabel || null,
            status: slot.matchStatus || slot.status || "pending",
            sets: slot.sets || [],
          } as PublicTournamentBracketMatch;
          const game = gameFromMatch({
            categoryKey: categoryKey || "categoria",
            categoryName,
            match,
            slot,
            fallbackIndex: index,
          });
          if (game) gamesById.set(game.id, game);
        });

        bracketResults.filter(Boolean).forEach((entry, entryIndex) => {
          if (!entry) return;
          const category = entry.category;
          const bracket = entry.bracket;
          const categoryKey =
            String(bracket.categoryKey || category.key || "")
              .trim()
              .toLowerCase() ||
            categoryKeyByName.get(category.name) ||
            `categoria-${entryIndex + 1}`;
          const categoryName = category.name;
          bracket.rounds.forEach((round, roundIndex) => {
            round.matches.forEach((match, matchIndex) => {
              const game = gameFromMatch({
                categoryKey,
                categoryName,
                match,
                slot: null,
                fallbackIndex: roundIndex * 100 + matchIndex,
              });
              if (game && !gamesById.has(game.id)) gamesById.set(game.id, game);
            });
          });
        });

        const nextGames = Array.from(gamesById.values()).sort((left, right) => {
          if (left.isFinished !== right.isFinished)
            return left.isFinished ? 1 : -1;
          return left.timestamp - right.timestamp;
        });
        setMyTournamentGames(nextGames);
      })
      .catch(() => {
        if (!active) return;
        setMyTournamentGamesError(challengeDetailTexts.myGames.loadError);
        setMyTournamentGames([]);
      })
      .finally(() => {
        if (active) setMyTournamentGamesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    challengeId,
    hasActiveMyRegistration,
    isTournamentChallenge,
    myGamesOpen,
    myPairLabel,
    myRegistration?.id,
    token,
    tournamentRegistrationCategoryOptions,
  ]);

  if (loading) {
    return <LoadingPage label={siteTexts.common.loading} />;
  }

  if (!displayedChallenge) {
    return (
      <AppLayout>
        <EmptyState
          message={loadError || challengeDetailTexts.states.notFound}
          action={
            <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
              <Button
                type="button"
                onClick={() => void fetchChallenge({ showLoading: true })}
              >
                {challengeDetailTexts.actions.retry}
              </Button>
              <Button href={listBasePath}>
                {challengeDetailTexts.actions.back}
              </Button>
            </div>
          }
        />
      </AppLayout>
    );
  }

  const location =
    String(displayedChallenge.address || "").trim() ||
    String(challengeLink?.address || "").trim() ||
    challengeDetailTexts.labels.locationFallback;
  const organizer =
    String(displayedChallenge.organizerName || "").trim() ||
    siteTexts.challenges.publicList.labels.organizerFallback;
  const challengeTitle =
    String(displayedChallenge.challengeName || "").trim() ||
    challengeDetailTexts.labels.challengeFallback;
  const challengeDescription = String(
    displayedChallenge.description || "",
  ).trim();
  const shouldCollapseAbout = challengeDescription.length > 180;
  const roundsDetailed = activeChallengeRoundsDetailed;
  const hasMatches = roundsDetailed.length > 0;
  const myRegistrationActive = hasActiveMyRegistration;
  const myChallengePairIds = (() => {
    if (!displayedChallenge || !user) return new Set<string>();
    const ids = new Set<string>();
    const userCpf = normalizeCpfDigits(user.cpf);
    const teammateCpf = normalizeCpfDigits(myRegistration?.teammateCpf);
    if (userCpf) ids.add(userCpf);
    (displayedChallenge.pairs || []).forEach((pair) => {
      const cpf1 = normalizeCpfDigits(pair.cpf1);
      const cpf2 = normalizeCpfDigits(pair.cpf2);
      if (!cpf1 && !cpf2) return;
      const userInPair = userCpf && (cpf1 === userCpf || cpf2 === userCpf);
      const teammateInPair =
        teammateCpf && (cpf1 === teammateCpf || cpf2 === teammateCpf);
      if (userInPair || teammateInPair) ids.add(normalizePairId(pair.id));
    });
    return ids;
  })();
  const myChallengeGames = (() => {
    if (!displayedChallenge || isTournamentChallenge || !myRegistrationActive) {
      return [];
    }
    return activeChallengeRoundsDetailed
      .flatMap((round, roundIndex) =>
        round.flatMap((match, matchIndex) => {
          const sideAId = normalizePairId(match.dupla1Id);
          const sideBId = normalizePairId(match.dupla2Id);
          const isSideA = myChallengePairIds.has(sideAId);
          const isSideB = myChallengePairIds.has(sideBId);
          if (!isSideA && !isSideB) return [];
          const score1 = parseScore(match.score1);
          const score2 = parseScore(match.score2);
          const status = myGameStatusMeta(
            score1 !== null && score2 !== null && score1 !== score2
              ? "finished"
              : "pending",
          );
          const date = myGameDateParts(displayedChallenge.scheduledAt);
          const pairLabel =
            (isSideA ? match.dupla1Label : match.dupla2Label) || myPairLabel;
          const opponentLabel =
            (isSideA ? match.dupla2Label : match.dupla1Label) || "A definir";
          return [
            {
              id: `challenge-${roundIndex}-${matchIndex}`,
              categoryName: challengeCategory,
              pairLabel,
              opponentLabel,
              phaseLabel:
                displayedChallenge.challengeType === "poco" &&
                roundIndex === activeChallengeRoundsDetailed.length - 1
                  ? challengeDetailTexts.myGames.final
                  : `${challengeDetailTexts.myGames.roundPrefix} ${roundIndex + 1}`,
              dateLabel: date.dateLabel,
              timeLabel: date.hasSchedule
                ? date.timeLabel
                : challengeDetailTexts.myGames.timeFallback,
              courtLabel: `${challengeDetailTexts.myGames.courtPrefix} ${matchIndex + 1}`,
              statusLabel: status.label,
              statusKey: status.key,
              timestamp: date.timestamp + matchIndex,
              isFinished: status.finished,
              hasSchedule: date.hasSchedule,
            },
          ];
        }),
      )
      .sort((left, right) => {
        if (left.isFinished !== right.isFinished)
          return left.isFinished ? 1 : -1;
        return left.timestamp - right.timestamp;
      });
  })();
  const myGames = isTournamentChallenge ? myTournamentGames : myChallengeGames;
  const nextMyGame =
    myGames.find((game) => !game.isFinished && game.hasSchedule) || null;
  const shareUrl =
    typeof window === "undefined"
      ? `https://todosnopadel.com.br/s/${origin === "tournaments" ? "torneios" : "desafios"}/${challengeId}`
      : buildCompetitionShareUrl(
          challengeId,
          origin === "tournaments" ? "tournament" : "challenge",
        );
  const shareCategoryLabel =
    challengeCategory &&
    challengeCategory !== challengeDetailTexts.labels.toBeDefined
      ? challengeCategory
      : "";
  const shareHasActiveRegistration =
    hasRegistration &&
    myRegistration?.paymentStatus !== "canceled" &&
    myRegistration?.paymentStatus !== "refunded";
  const shareParticipant =
    shareHasActiveRegistration && user
      ? {
          name:
            String(user.name || "").trim() ||
            challengeDetailTexts.labels.playerFallback,
          avatarUrl: String(user.avatarUrl || "").trim() || null,
          partnerName:
            String(myRegistration?.teammateName || "").trim() || null,
          categoryLabel:
            String(myRegistration?.registrationCategoryName || "").trim() ||
            shareCategoryLabel ||
            null,
        }
      : null;
  const challengeShareData: ChallengeShareData = {
    title: challengeTitle,
    eventType: challengeFormatLabel(displayedChallenge.challengeType),
    dateLabel: subtitleDate || challengeDetailTexts.labels.toBeDefined,
    location,
    organizer,
    eventIdentityMode:
      displayedChallenge.eventIdentityMode ||
      (displayedChallenge.isOfficial ? "tnp_standard" : "organizer_photo"),
    organizerAvatarUrl: displayedChallenge.organizerAvatarUrl || null,
    organizerLogoUrl:
      displayedChallenge.organizerLogoUrl ||
      organizerVisualIdentity?.logoUrl ||
      null,
    totalSlots: `${summarySlotsLimitForDisplay} ${summarySlotsDisplayUnit}`,
    availableSlots: `${summarySlotsRemainingForDisplay} ${summarySlotsDisplayUnit}`,
    priceLabel: amountPerPlayerLabel,
    categoryLabel: shareCategoryLabel || undefined,
    shareUrl,
    participant: shareParticipant,
  };
  const organizerVisualIdentityStyle = organizerIdentityStyle(
    organizerVisualIdentity,
  );
  const renderCtaButton = (className: string) => {
    if (!ctaState.visible || !mobileCtaButtonLabel) return null;
    if (ctaState.disabled) {
      return (
        <Button variant="primary" className={className} type="button" disabled>
          {mobileCtaButtonLabel}
        </Button>
      );
    }
    if (ctaState.targetTab) {
      return (
        <Button
          variant="primary"
          className={className}
          type="button"
          onClick={() => {
            const nextTab = ctaState.targetTab;
            if (!nextTab) return;
            setActiveTab(nextTab);
            window.setTimeout(() => {
              tabContentRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 0);
          }}
        >
          {mobileCtaButtonLabel}
          <span className="material-symbols-outlined" aria-hidden="true">
            arrow_forward
          </span>
        </Button>
      );
    }
    return (
      <Button variant="primary" className={className} href={ctaState.href}>
        {mobileCtaButtonLabel}
        <span className="material-symbols-outlined" aria-hidden="true">
          arrow_forward
        </span>
      </Button>
    );
  };
  const renderTitleCta = () => {
    if (!ctaState.visible || !mobileCtaButtonLabel) return null;
    if (!ctaState.showInfo) {
      return (
        <div className="challenge-public-title-status challenge-public-title-status--action">
          {renderCtaButton("challenge-public-hero-cta-btn")}
        </div>
      );
    }
    return (
      <div
        className={`challenge-public-title-status challenge-public-title-status--${
          ctaState.variant || "default"
        }`}
      >
        <div>
          <strong>{ctaState.title}</strong>
          <span>{ctaState.footer || ctaState.text}</span>
        </div>
        {renderCtaButton("challenge-public-hero-cta-btn")}
      </div>
    );
  };

  return (
    <AppLayout>
      <article
        className={`challenge-public-page${isTournamentChallenge ? " is-tournament" : ""}${
          isMobileInfoOpen ? " is-mobile-info-expanded" : ""
        }`}
        style={organizerVisualIdentityStyle}
      >
        <section className="challenge-public-hero">
          <div className="challenge-public-hero-main">
            <div className="challenge-public-status-row">
              <span
                className={`challenge-public-status-badge challenge-public-status-badge--${challengeStatusMeta.tone}`}
              >
                {challengeStatusMeta.label}
              </span>
              {Boolean(displayedChallenge.includeInRanking) && (
                <span
                  className="challenge-public-ranking-badge"
                  title={siteTexts.challenges.publicList.labels.rankingIncluded}
                  aria-label={
                    siteTexts.challenges.publicList.labels.rankingIncluded
                  }
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    emoji_events
                  </span>
                </span>
              )}
              <button
                type="button"
                className="challenge-public-share-button"
                onClick={() => setShareModalOpen(true)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  ios_share
                </span>
                {challengeDetailTexts.shareAction}
              </button>
              {hasActiveMyRegistration && (
                <button
                  type="button"
                  className="challenge-public-my-games-button"
                  onClick={() => setMyGamesOpen(true)}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    event_note
                  </span>
                  {challengeDetailTexts.myGames.title}
                </button>
              )}
            </div>
            <div className="challenge-public-title-row">
              <h1>
                <span className="challenge-public-hero-title-text">
                  {challengeTitle}
                </span>
              </h1>
              {renderTitleCta()}
            </div>
            <div className="challenge-public-hero-meta-row">
              <p className="challenge-public-hero-meta-pill">
                <span className="material-symbols-outlined" aria-hidden="true">
                  calendar_today
                </span>
                {subtitleDate}
              </p>
              <p className="challenge-public-hero-meta-pill">
                <EventLocationActions
                  address={
                    String(displayedChallenge.address || "").trim() ||
                    String(challengeLink?.address || "").trim()
                  }
                  city={
                    String(displayedChallenge.city || "").trim() ||
                    String(challengeLink?.city || "").trim()
                  }
                  state={
                    String(displayedChallenge.state || "").trim() ||
                    String(challengeLink?.state || "").trim()
                  }
                  fallback={challengeDetailTexts.labels.locationFallback}
                  showLeadingIcon
                  valueAs="span"
                  variant="pill"
                />
              </p>
              <p className="challenge-public-hero-meta-pill">
                <span className="material-symbols-outlined" aria-hidden="true">
                  corporate_fare
                </span>
                {organizer}
              </p>
            </div>

            <button
              type="button"
              className="challenge-public-mobile-info-toggle"
              aria-expanded={isMobileInfoOpen}
              onClick={() => setIsMobileInfoOpen((current) => !current)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                info
              </span>
              <strong>{challengeDetailTexts.labels.moreInformation}</strong>
              <span
                className="material-symbols-outlined challenge-public-mobile-info-chevron"
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>

            <div className="challenge-public-mobile-extra-content">
              <div className="challenge-public-key-facts">
                <article className="challenge-public-key-fact">
                  <span
                    className="challenge-public-key-fact-icon challenge-public-key-fact-icon--groups"
                    aria-hidden="true"
                  >
                    <span className="material-symbols-outlined">groups</span>
                  </span>
                  <div>
                    <strong>{summaryRegistrationsForDisplay}</strong>
                    <span>{summarySlotsDisplayUnit}</span>
                  </div>
                </article>
                <article className="challenge-public-key-fact">
                  <span
                    className="challenge-public-key-fact-icon challenge-public-key-fact-icon--paid"
                    aria-hidden="true"
                  >
                    <span className="material-symbols-outlined">paid</span>
                  </span>
                  <div>
                    <strong>{amountPerPlayerLabel}</strong>
                    <span>{challengeDetailTexts.about.labels.perPlayer}</span>
                  </div>
                </article>
                <article className="challenge-public-key-fact">
                  <span
                    className="challenge-public-key-fact-icon challenge-public-key-fact-icon--format"
                    aria-hidden="true"
                  >
                    <span className="material-symbols-outlined">
                      {isTournamentChallenge ? "swords" : "sports_tennis"}
                    </span>
                  </span>
                  <div>
                    <strong>
                      {isTournamentChallenge
                        ? challengeDetailTexts.labels.groupStage
                        : challengeCategory}
                    </strong>
                    <span>
                      {isTournamentChallenge
                        ? challengeDetailTexts.labels.format
                        : challengeFormatLabel(displayedChallenge.challengeType)}
                    </span>
                  </div>
                </article>
              </div>

              {!isTournamentChallenge &&
                tournamentRegistrationCategoryOptions.length > 0 && (
                  <div className="challenge-public-category-times">
                    {tournamentRegistrationCategoryOptions.map((category) => (
                      <article key={`challenge-category-time-${category.key}`}>
                        <strong>{category.name}</strong>
                        <span>
                          {category.timeLabel ||
                            challengeDetailTexts.myGames.timeFallback}
                        </span>
                      </article>
                    ))}
                  </div>
                )}

              {challengeStatusMeta.tone === "canceled" && (
                <div className="challenge-public-canceled-alert">
                  {challengeDetailTexts.states.canceledAlert}
                </div>
              )}
            </div>
          </div>

        </section>

        <section className="challenge-public-body-grid">
          <div className="challenge-public-main-column" ref={tabContentRef}>
            <div
              className="challenge-public-tabs"
              role="tablist"
              aria-label={challengeDetailTexts.tabs.ariaLabel}
            >
              <button
                type="button"
                className={`challenge-public-tab${activeTab === "registrations" ? " is-active" : ""}`}
                onClick={() => setActiveTab("registrations")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  groups
                </span>
                {challengeDetailTexts.tabs.registrations}
              </button>
              {!isTournamentChallenge && (
                <button
                  type="button"
                  className={`challenge-public-tab${activeTab === "matches" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("matches")}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    sports_tennis
                  </span>
                  {challengeDetailTexts.tabs.matches}
                </button>
              )}
              {isTournamentChallenge && (
                <button
                  type="button"
                  className={`challenge-public-tab${activeTab === "brackets" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("brackets")}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    account_tree
                  </span>
                  {challengeDetailTexts.tabs.brackets}
                </button>
              )}
              {isTournamentChallenge && hasPublicTournamentEventSchedule && (
                <button
                  type="button"
                  className={`challenge-public-tab${activeTab === "eventSchedule" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("eventSchedule")}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    calendar_month
                  </span>
                  {challengeDetailTexts.tabs.eventSchedule}
                </button>
              )}
              {isTournamentChallenge && (
                <button
                  type="button"
                  className={`challenge-public-tab${activeTab === "timeline" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("timeline")}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    timeline
                  </span>
                  {challengeDetailTexts.tabs.timeline}
                </button>
              )}
              {!isTournamentChallenge && (
                <button
                  type="button"
                  className={`challenge-public-tab${activeTab === "ranking" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("ranking")}
                >
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    leaderboard
                  </span>
                  {challengeDetailTexts.tabs.ranking}
                </button>
              )}
            </div>

            {hasRegistrationCategories &&
              activeTab === "registrations" &&
              tournamentRegistrationCategoryOptions.length > 0 && (
                <div className="challenge-public-category-toolbar">
                  <div className="challenge-public-category-toolbar-field">
                    <span>{challengeDetailTexts.labels.categoryFallback}</span>
                    <CategorySelectDropdown
                      ariaLabel={challengeDetailTexts.labels.categoryFilterAria}
                      options={registrationCategorySelectOptions}
                      value={registrationTournamentCategoryKey}
                      onChange={setSelectedTournamentCategoryKey}
                    />
                  </div>
                </div>
              )}

            {!isTournamentChallenge &&
              hasRegistrationCategories &&
              (activeTab === "matches" || activeTab === "ranking") &&
              tournamentRegistrationCategoryOptions.length > 1 && (
                <div className="challenge-public-category-toolbar">
                  <div className="challenge-public-category-toolbar-field">
                    <span>{challengeDetailTexts.labels.categoryFallback}</span>
                    <CategorySelectDropdown
                      ariaLabel={challengeDetailTexts.labels.categoryFilterAria}
                      options={bracketCategorySelectOptions}
                      value={activeChallengeRoundsCategoryKey}
                      onChange={setSelectedTournamentCategoryKey}
                    />
                  </div>
                </div>
              )}

            {activeTab === "registrations" && (
              <section className="challenge-public-panel">
                <header className="challenge-public-panel-head">
                  <h3>
                    <span className="challenge-public-panel-title-icon">
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        groups
                      </span>
                    </span>
                    <span>
                      {challengeDetailTexts.registration.title}{" "}
                      <em>
                        ({registrationPanelParticipantsCount}/
                        {registrationPanelSlotsLimit})
                      </em>
                    </span>
                  </h3>
                </header>

                <div className="challenge-public-table-wrap">
                  <table className="challenge-public-table challenge-public-table--public">
                    <thead>
                      <tr>
                        <th>
                          {registrationPanelIsFixedPair
                            ? challengeDetailTexts.registration.table.pair
                            : challengeDetailTexts.registration.table.player}
                        </th>
                        <th>
                          {challengeDetailTexts.registration.table.status}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParticipantsRows.map((participant) => (
                        <tr key={participant.key}>
                          <td>
                            <div
                              className={`challenge-public-player-cell${
                                participant.avatars.length > 1 ? " is-pair" : ""
                              }`}
                            >
                              {participant.avatars.length > 1 ? (
                                <>
                                  <span className="challenge-public-player-avatars">
                                    {participant.avatars
                                      .slice(0, 2)
                                      .map((avatar, avatarIndex) => {
                                        const avatarNode = (
                                          <UserAvatar
                                            className="challenge-public-player-avatar"
                                            name={avatar.name}
                                            sex={avatar.sex}
                                            avatarUrl={avatar.avatarUrl || ""}
                                            size={50}
                                          />
                                        );
                                        return avatar.publicPlayerId ? (
                                          <a
                                            key={`${participant.key}-avatar-${avatarIndex}`}
                                            className="challenge-public-player-link"
                                            href={`#/jogadores/${avatar.publicPlayerId}`}
                                          >
                                            {avatarNode}
                                          </a>
                                        ) : (
                                          <span key={`${participant.key}-avatar-${avatarIndex}`}>
                                            {avatarNode}
                                          </span>
                                        );
                                      })}
                                  </span>
                                  <span className="challenge-public-pair-names">
                                    {participant.avatars
                                      .slice(0, 2)
                                      .map((avatar, avatarIndex) => (
                                        <span
                                          key={`${participant.key}-member-${avatarIndex}`}
                                          className="challenge-public-pair-member"
                                        >
                                          {avatar.publicPlayerId ? (
                                            <a
                                              className="challenge-public-player-link"
                                              href={`#/jogadores/${avatar.publicPlayerId}`}
                                            >
                                              {avatar.name}
                                            </a>
                                          ) : (
                                            avatar.name
                                          )}
                                        </span>
                                      ))}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="challenge-public-player-avatars">
                                    {participant.avatars[0]?.publicPlayerId ? (
                                      <a
                                        className="challenge-public-player-link"
                                        href={`#/jogadores/${participant.avatars[0].publicPlayerId}`}
                                      >
                                        <UserAvatar
                                          className="challenge-public-player-avatar"
                                          name={participant.avatars[0]?.name}
                                          sex={participant.avatars[0]?.sex}
                                          avatarUrl={
                                            participant.avatars[0]?.avatarUrl || ""
                                          }
                                          size={50}
                                        />
                                      </a>
                                    ) : (
                                      <UserAvatar
                                      className="challenge-public-player-avatar"
                                      name={participant.avatars[0]?.name}
                                      sex={participant.avatars[0]?.sex}
                                      avatarUrl={
                                        participant.avatars[0]?.avatarUrl || ""
                                      }
                                      size={50}
                                      />
                                    )}
                                  </span>
                                  {participant.avatars[0]?.publicPlayerId ? (
                                    <a
                                      className="challenge-public-player-link"
                                      href={`#/jogadores/${participant.avatars[0].publicPlayerId}`}
                                    >
                                      {participant.name}
                                    </a>
                                  ) : (
                                    <span>{participant.name}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`challenge-public-registration-status-badge challenge-public-registration-status-badge--${participant.statusTone || "pending"}`}
                            >
                              {participant.statusLabel}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredParticipantsRows.length === 0 && (
                  <div className="challenge-public-empty-state challenge-public-empty-state--registrations">
                    <span
                      className="challenge-public-empty-icon"
                      aria-hidden="true"
                    >
                      <span className="material-symbols-outlined">groups</span>
                    </span>
                    <h4>{challengeDetailTexts.states.noRegistrations}</h4>
                    <p>{challengeDetailTexts.states.noRegistrationsText}</p>
                  </div>
                )}
              </section>
            )}

            {isTournamentChallenge &&
              activeTab === "brackets" &&
              tournamentRegistrationCategoryOptions.length > 1 && (
                <div className="challenge-public-category-toolbar">
                  <div className="challenge-public-category-toolbar-field">
                    <span>{challengeDetailTexts.labels.categoryFallback}</span>
                    <CategorySelectDropdown
                      ariaLabel={challengeDetailTexts.labels.categoryFilterAria}
                      options={bracketCategorySelectOptions}
                      value={activeTournamentCategoryKey}
                      onChange={setSelectedTournamentCategoryKey}
                    />
                  </div>
                </div>
              )}

            {isTournamentChallenge && activeTab === "brackets" && (
              <PublicTournamentProgressPanel
                tournamentId={challengeId}
                token={token || undefined}
                mode="brackets"
                eventTitle={displayedChallenge.challengeName}
                eventDate={displayedChallenge.scheduledAt || null}
                eventLocation={displayedChallenge.address || ""}
                categories={tournamentRegistrationCategoryOptions.map(
                  (category) => ({
                    categoryKey: category.key,
                    categoryName: category.name,
                  }),
                )}
                selectedCategoryKey={activeTournamentCategoryKey}
                onSelectedCategoryKeyChange={setSelectedTournamentCategoryKey}
                showInternalCategoryTabs={false}
              />
            )}

            {isTournamentChallenge &&
              activeTab === "eventSchedule" &&
              tournamentEventSchedule && (
                <TournamentEventScheduleView
                  schedule={tournamentEventSchedule}
                  texts={challengeDetailTexts.eventSchedule}
                />
              )}

            {isTournamentChallenge && activeTab === "timeline" && (
              <PublicTournamentProgressPanel
                tournamentId={challengeId}
                token={token || undefined}
                mode="timeline"
                eventTitle={displayedChallenge.challengeName}
                eventDate={displayedChallenge.scheduledAt || null}
                eventLocation={displayedChallenge.address || ""}
                categories={tournamentRegistrationCategoryOptions.map(
                  (category) => ({
                    categoryKey: category.key,
                    categoryName: category.name,
                  }),
                )}
              />
            )}

            {!isTournamentChallenge && activeTab === "matches" && (
              <section className="challenge-public-panel">
                <header className="challenge-public-panel-head">
                  <h3>{challengeDetailTexts.matches.title}</h3>
                </header>

                {challengeStatusMeta.tone === "pending" && !hasMatches ? (
                  <div className="challenge-public-empty-state">
                    <h4>
                      {challengeDetailTexts.states.noMatchesGeneratedTitle}
                    </h4>
                    <p>{challengeDetailTexts.states.noMatchesGeneratedText}</p>
                  </div>
                ) : !hasMatches ? (
                  <div className="challenge-public-empty-state">
                    <h4>{challengeDetailTexts.states.noMatchesTitle}</h4>
                    <p>{challengeDetailTexts.states.noMatchesText}</p>
                  </div>
                ) : (
                  <div className="challenge-public-rounds">
                    {roundsDetailed.map((round, roundIndex) => {
                      const roundGroup = resolveSuper8RoundGroup(
                        roundIndex,
                        roundsDetailed.length,
                        displayedChallenge.challengeType === "super8",
                        Number(
                          displayedChallenge.participantsCount ||
                            displayedChallenge.participantCpfs?.length ||
                            0,
                        ),
                        String(displayedChallenge.status || ""),
                      );
                      return (
                        <article
                          key={`round-${roundIndex}`}
                          className="challenge-public-round-card"
                        >
                          <div className="challenge-public-round-head">
                            <h4>
                              {displayedChallenge.challengeType === "poco" &&
                              roundIndex === roundsDetailed.length - 1
                                ? challengeDetailTexts.matches.final
                                : `${challengeDetailTexts.matches.round} ${roundIndex + 1}`}
                            </h4>
                            {roundGroup && (
                              <span>
                                {challengeDetailTexts.matches.group}{" "}
                                {roundGroup}
                              </span>
                            )}
                          </div>
                          <div className="challenge-public-matches">
                            {displayedChallenge.challengeType === "poco" &&
                              round.length > 0 &&
                              round.every((match) => {
                                const pair1BaseLabel = isPendingPairLabel(
                                  match.dupla1Label,
                                )
                                  ? pairLabelById[
                                      normalizePairId(match.dupla1Id)
                                    ] || match.dupla1Label
                                  : match.dupla1Label;
                                const pair2BaseLabel = isPendingPairLabel(
                                  match.dupla2Label,
                                )
                                  ? pairLabelById[
                                      normalizePairId(match.dupla2Id)
                                    ] || match.dupla2Label
                                  : match.dupla2Label;
                                return (
                                  isPendingPocoMatch(match) &&
                                  isPendingPairLabel(pair1BaseLabel) &&
                                  isPendingPairLabel(pair2BaseLabel)
                                );
                              }) && (
                                <div className="challenge-public-match-row challenge-public-match-row-pending">
                                  <span className="challenge-public-match-pair">
                                    {
                                      siteTexts.challengeEngine.labels
                                        .removedPair
                                    }
                                  </span>
                                </div>
                              )}
                            {round.map((match, matchIndex) => {
                              const score1 = parseScore(match.score1);
                              const score2 = parseScore(match.score2);
                              const pair1BaseLabel = isPendingPairLabel(
                                match.dupla1Label,
                              )
                                ? pairLabelById[
                                    normalizePairId(match.dupla1Id)
                                  ] || match.dupla1Label
                                : match.dupla1Label;
                              const pair2BaseLabel = isPendingPairLabel(
                                match.dupla2Label,
                              )
                                ? pairLabelById[
                                    normalizePairId(match.dupla2Id)
                                  ] || match.dupla2Label
                                : match.dupla2Label;
                              const isPendingMatch =
                                displayedChallenge.challengeType === "poco" &&
                                isPendingPocoMatch(match) &&
                                isPendingPairLabel(pair1BaseLabel) &&
                                isPendingPairLabel(pair2BaseLabel);
                              if (isPendingMatch) {
                                return null;
                              }
                              const pocoWinnerSide =
                                displayedChallenge.challengeType === "poco"
                                  ? resolvePocoWinnerSide(match)
                                  : null;
                              const isPocoFinalRound =
                                displayedChallenge.challengeType === "poco" &&
                                roundIndex === roundsDetailed.length - 1;
                              const pair1Points = Number(
                                pocoPointsByPairId[
                                  normalizePairId(match.dupla1Id)
                                ],
                              );
                              const pair2Points = Number(
                                pocoPointsByPairId[
                                  normalizePairId(match.dupla2Id)
                                ],
                              );
                              const pair1Label =
                                isPocoFinalRound && Number.isFinite(pair1Points)
                                  ? `${pair1BaseLabel} (${pair1Points} ${challengeDetailTexts.matches.pointsSuffix})`
                                  : pair1BaseLabel;
                              const pair2Label =
                                isPocoFinalRound && Number.isFinite(pair2Points)
                                  ? `${pair2BaseLabel} (${pair2Points} ${challengeDetailTexts.matches.pointsSuffix})`
                                  : pair2BaseLabel;
                              const isMatchFinished =
                                score1 !== null &&
                                score2 !== null &&
                                score1 !== score2;
                              const isMatchOnCourt =
                                String(match.status || "")
                                  .trim()
                                  .toLowerCase() === "in_progress";
                              const isMatchPartial =
                                (score1 !== null && score2 === null) ||
                                (score1 === null && score2 !== null) ||
                                (score1 !== null &&
                                  score2 !== null &&
                                  score1 === score2);
                              return (
                                <div
                                  key={`match-${roundIndex}-${matchIndex}`}
                                  className="challenge-public-match-row"
                                >
                                  <ChallengeMatchParticipant
                                    avatars={
                                      pairAvatarsById[
                                        normalizePairId(match.dupla1Id)
                                      ] || []
                                    }
                                    label={pair1Label}
                                    side="first"
                                  />
                                  {displayedChallenge.challengeType ===
                                  "poco" ? (
                                    <div
                                      className="challenge-public-match-score challenge-public-match-score--poco"
                                      aria-label={
                                        challengeDetailTexts.matches
                                          .matchResultAria
                                      }
                                    >
                                      <span
                                        className={`challenge-public-poco-icon ${
                                          pocoWinnerSide === "dupla1"
                                            ? "winner"
                                            : pocoWinnerSide === "dupla2"
                                              ? "loser"
                                              : "pending"
                                        }`}
                                        aria-label={
                                          pocoWinnerSide === "dupla1"
                                            ? challengeDetailTexts.matches
                                                .champion
                                            : pocoWinnerSide === "dupla2"
                                              ? challengeDetailTexts.matches
                                                  .loser
                                              : challengeDetailTexts.matches
                                                  .awaitingResult
                                        }
                                      >
                                        {pocoWinnerSide === "dupla1"
                                          ? "🏆"
                                          : pocoWinnerSide === "dupla2"
                                            ? "❌"
                                            : "⏳"}
                                      </span>
                                      <small>x</small>
                                      <span
                                        className={`challenge-public-poco-icon ${
                                          pocoWinnerSide === "dupla2"
                                            ? "winner"
                                            : pocoWinnerSide === "dupla1"
                                              ? "loser"
                                              : "pending"
                                        }`}
                                        aria-label={
                                          pocoWinnerSide === "dupla2"
                                            ? challengeDetailTexts.matches
                                                .champion
                                            : pocoWinnerSide === "dupla1"
                                              ? challengeDetailTexts.matches
                                                  .loser
                                              : challengeDetailTexts.matches
                                                  .awaitingResult
                                        }
                                      >
                                        {pocoWinnerSide === "dupla2"
                                          ? "🏆"
                                          : pocoWinnerSide === "dupla1"
                                            ? "❌"
                                            : "⏳"}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="challenge-public-match-score">
                                      <strong>
                                        {score1 === null ? "-" : score1}
                                      </strong>
                                      <small>x</small>
                                      <strong>
                                        {score2 === null ? "-" : score2}
                                      </strong>
                                    </div>
                                  )}
                                  <ChallengeMatchParticipant
                                    avatars={
                                      pairAvatarsById[
                                        normalizePairId(match.dupla2Id)
                                      ] || []
                                    }
                                    label={pair2Label}
                                    side="second"
                                  />
                                  <span
                                    className={`challenge-public-match-status${
                                      isMatchFinished
                                        ? " is-finished"
                                        : isMatchOnCourt
                                          ? " is-in-progress"
                                          : isMatchPartial
                                          ? " is-partial"
                                          : " is-waiting"
                                    }`}
                                  >
                                    {isMatchFinished
                                      ? challengeDetailTexts.matches.status
                                          .finished
                                      : isMatchOnCourt
                                        ? siteTexts.challengeEngine.courtStatus
                                            .onCourt
                                        : isMatchPartial
                                        ? challengeDetailTexts.matches.status
                                            .partial
                                        : challengeDetailTexts.matches.status
                                            .waiting}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {!isTournamentChallenge && activeTab === "ranking" && (
              <section className="challenge-public-panel">
                <header className="challenge-public-panel-head challenge-public-panel-head--row">
                  <h3>{challengeDetailTexts.ranking.title}</h3>
                </header>

                {challengeStatusMeta.tone === "pending" &&
                rankingRows.length === 0 ? (
                  <div className="challenge-public-empty-state">
                    <h4>{challengeDetailTexts.states.noRankingTitle}</h4>
                    <p>{challengeDetailTexts.states.noRankingText}</p>
                  </div>
                ) : rankingRows.length === 0 ? (
                  <div className="challenge-public-empty-state">
                    <h4>{challengeDetailTexts.states.noRankingResultsTitle}</h4>
                    <p>{challengeDetailTexts.states.noRankingResultsText}</p>
                  </div>
                ) : (
                  <>
                    <ResponsiveRankingTable
                      className="challenge-public-ranking-table"
                      ariaLabel={challengeDetailTexts.ranking.ariaLabel}
                      rankHeader={challengeDetailTexts.ranking.rankHeader}
                      titleHeader={
                        displayedChallenge.challengeType === "super8"
                          ? challengeDetailTexts.ranking.titleHeaderPlayer
                          : challengeDetailTexts.ranking.titleHeaderPair
                      }
                      columns={rankingColumns}
                      rows={rankingRows}
                    />
                  </>
                )}
              </section>
            )}
          </div>

          {challengeDescription && (
            <aside className="challenge-public-side-column">
              <section className="challenge-public-panel challenge-public-about">
                <div className="challenge-public-about-content">
                  <div
                    className={`challenge-public-about-summary-box${
                      isAboutExpanded || !shouldCollapseAbout
                        ? " is-expanded"
                        : ""
                    }`}
                  >
                    <p className="challenge-public-about-summary">
                      {challengeDescription}
                    </p>
                    {shouldCollapseAbout && (
                      <button
                        type="button"
                        className="challenge-public-about-toggle"
                        onClick={() => setIsAboutExpanded((current) => !current)}
                      >
                        {isAboutExpanded
                          ? challengeDetailTexts.about.labels.showLess
                          : challengeDetailTexts.about.labels.showMore}
                      </button>
                    )}
                  </div>
                </div>
              </section>
            </aside>
          )}
        </section>
      </article>
      {myGamesOpen && hasActiveMyRegistration && (
        <div
          className="challenge-my-games-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={challengeDetailTexts.myGames.title}
        >
          <section className="challenge-my-games-modal">
            <header className="challenge-my-games-head">
              <div className="challenge-my-games-title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  event_note
                </span>
                <div>
                  <h2>{challengeDetailTexts.myGames.title}</h2>
                  <p>{challengeDetailTexts.myGames.description}</p>
                </div>
              </div>
              <button
                type="button"
                className="challenge-my-games-close"
                onClick={() => setMyGamesOpen(false)}
                aria-label={challengeDetailTexts.myGames.closeAria}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  close
                </span>
              </button>
            </header>

            <div className="challenge-my-games-summary">
              <div>
                <span>{challengeDetailTexts.myGames.category}</span>
                <strong>
                  {String(
                    myRegistration?.registrationCategoryName || "",
                  ).trim() || challengeCategory}
                </strong>
              </div>
              <div>
                <span>{challengeDetailTexts.myGames.yourPair}</span>
                <strong>{myPairLabel}</strong>
              </div>
              <div className="challenge-my-games-next">
                <span>{challengeDetailTexts.myGames.nextGame}</span>
                <strong>
                  {nextMyGame
                    ? `${nextMyGame.timeLabel} - ${nextMyGame.courtLabel}`
                    : challengeDetailTexts.labels.toBeDefined}
                </strong>
              </div>
            </div>

            {myTournamentGamesLoading ? (
              <Spinner label={challengeDetailTexts.myGames.loading} />
            ) : myTournamentGamesError ? (
              <div className="challenge-public-empty-state">
                <h4>{myTournamentGamesError}</h4>
                <p>{challengeDetailTexts.myGames.retry}</p>
              </div>
            ) : myGames.length === 0 ? (
              <div className="challenge-public-empty-state">
                <h4>
                  {isTournamentChallenge
                    ? challengeDetailTexts.myGames.tournamentEmpty
                    : challengeDetailTexts.myGames.challengeEmpty}
                </h4>
                <p>{challengeDetailTexts.myGames.emptyHelp}</p>
              </div>
            ) : (
              <div className="challenge-my-games-list">
                {myGames.map((game, index) => (
                  <article
                    key={game.id}
                    className={`challenge-my-game-card is-${game.statusKey}${
                      nextMyGame?.id === game.id ? " is-next" : ""
                    }`}
                  >
                    <div className="challenge-my-game-meta">
                      {nextMyGame?.id === game.id && (
                        <span className="challenge-my-game-next-badge">
                          {challengeDetailTexts.myGames.nextGame}
                        </span>
                      )}
                      <strong>
                        {challengeDetailTexts.myGames.gamePrefix} {index + 1}
                      </strong>
                      <span>{game.categoryName}</span>
                      <span>{game.phaseLabel}</span>
                    </div>
                    <div className="challenge-my-game-schedule">
                      <span>
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          calendar_today
                        </span>
                        {game.dateLabel}
                      </span>
                      <strong>{game.timeLabel}</strong>
                      <span>
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          sports_tennis
                        </span>
                        {game.courtLabel}
                      </span>
                    </div>
                    <div className="challenge-my-game-versus">
                      <strong>{game.pairLabel}</strong>
                      <span>x</span>
                      <strong>{game.opponentLabel}</strong>
                    </div>
                    <span
                      className={`challenge-my-game-status is-${game.statusKey}`}
                    >
                      {game.statusLabel}
                    </span>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
      <ChallengeShareModal
        open={shareModalOpen}
        data={challengeShareData}
        visualIdentity={organizerVisualIdentity}
        onClose={() => setShareModalOpen(false)}
      />
    </AppLayout>
  );
}
